import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { dnAlert, escapeHtml } from "./utils/dNews_Alert";
import Select from "react-select";
import ScrollSelect from "./ScrollSelect";
import { apiFetch } from "./utils/api";
import { dslipYears, DSLIP_YEAR_SELECT } from "./utils/dSlip_years";
import { numericInputProps } from "./utils/numericInput";
import { PAYSLIP_FONTS, DEFAULT_FONT_KEY, fontCssStyle, getFont } from "./utils/payslipFonts";
import { FaTimes, FaSearch, FaPlus, FaTrash, FaCheck, FaInfoCircle } from "react-icons/fa";
import logo_eagle from "./assets/img/logo_eagle.png";
import DOLLUZ_Full_Logo from "./assets/img/DOLLUZ_Full_Logo.png";
import signature_img from "./assets/img/signature.png";
import "./Payslip_Salary_Structure.css";

// Compact react-select styles tuned to fit inside the bordered payslip cells
const cellSelectStyles = (hasError) => ({
    // Force the outer wrapper to fill the cell - without this the control
    // shrinks to the width of its placeholder/value (the size that was changing).
    container: (base) => ({ ...base, width: "100%" }),
    control: (base) => ({
        ...base,
        minHeight: 30,
        height: 30,
        width: "100%",
        border: "none",
        boxShadow: "none",
        background: hasError ? "#fef2f2" : "transparent",
        outline: hasError ? "1.5px solid #dc2626" : "none",
        borderRadius: 4,
        cursor: "pointer",
    }),
    valueContainer: (base) => ({ ...base, padding: "0 6px", height: 30 }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: (base) => ({ ...base, height: 30 }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base) => ({ ...base, padding: 2, color: "#6b7280" }),
    // The modal has its own stacking context (z-index 1001). The portaled menu lives
    // outside the modal, so its z-index needs to win over the modal's overlay too.
    menuPortal: (base) => ({ ...base, zIndex: 10000 }),
    menu: (base) => ({ ...base, zIndex: 10000, fontSize: 13 }),
    // One row per option - long labels truncate with ellipsis instead of wrapping,
    // which keeps the menu height predictable and stops it overflowing the modal.
    option: (base, state) => ({
        ...base,
        padding: "6px 12px",
        fontSize: 13,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        background: state.isSelected ? "#4a90e2" : state.isFocused ? "#e8f0fe" : "#fff",
        color: state.isSelected ? "#fff" : "#1f2937",
        cursor: "pointer",
    }),
    placeholder: (base) => ({ ...base, color: "#9ca3af", fontSize: 13 }),
    singleValue: (base) => ({ ...base, color: "#1f2937", fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }),
});

// Month / Year pickers for the "Effective From - To" rows. Mirrors the
// dropdowns in Payslip Management (40px control, slate border, orange focus
// ring, styled menu) so every dSlip date picker looks the same - a native
// <select> can't style its popup list, which is what made these look plain.
// Widths match PM_MONTH_STYLES / PM_YEAR_STYLES exactly (150 / 110).
const rangeSelectStyles = (width, hasError) => ({
    container: (base) => ({ ...base, width }),
    control: (base, state) => ({
        ...base,
        minHeight: 40,
        borderRadius: 8,
        borderColor: hasError ? "#dc2626" : state.isFocused ? "#fb923c" : "#cbd5e1",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(251, 146, 60, 0.15)" : "none",
        "&:hover": { borderColor: hasError ? "#dc2626" : "#fb923c" },
    }),
    option: (base, state) => ({
        ...base,
        padding: "10px 12px",
        backgroundColor: state.isDisabled
            ? "#f8fafc"
            : state.isSelected ? "#4a90e2" : state.isFocused ? "#f3f4f6" : "#fff",
        color: state.isDisabled ? "#94a3b8" : state.isSelected ? "#fff" : "#0d1b2a",
        cursor: state.isDisabled ? "not-allowed" : "pointer",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    // These live inside the Configure modal (z-index 1001); the portaled menu
    // renders outside it, so it has to out-rank the modal overlay.
    menuPortal: (base) => ({ ...base, zIndex: 10000 }),
    menu: (base) => ({ ...base, zIndex: 10000 }),
});
const MONTH_SEL_W = 150;
const YEAR_SEL_W = 110;

// Shared props for all the Effective From - To pickers.
//   menuPlacement "auto" - this row sits at the BOTTOM of the Configure modal,
//   so a menu that always opened downward ran off the screen. "auto" flips it
//   above the control when there isn't room below.
//   maxMenuHeight caps it so a 33-year list can't fill the viewport either way.
// menuPlacement is passed explicitly rather than left on "auto". With a
// portaled menu, react-select measures the space below against the scroll
// parent - and the long employee table behind the modal makes it think there
// is room, so "auto" never flipped and the menu ran off the screen. The
// Effective From - To row is pinned to the modal footer, so "top" is always
// right there; the bulk/template copy sits mid-form and keeps "auto".
const rangeSelectProps = (menuPlacement) => ({
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    menuPlacement,
    maxMenuHeight: 240,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) =>
    Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Defaults used only when creating the very first structure for an employee
const DEFAULT_NOTE = "Income Tax will be deducted as per income Tax Act 1961 and will be borne by the employee";
const DEFAULT_ADDRESS = "#254/3, Sree Narayana Complex, C Block,\nSarathy Nagar, Velachery, Chennai 600042";

// Mirror Employee Center's LOCATIONS list
const LOCATIONS = ["Chennai", "Hyderabad", "Pune", "Nagpur", "Mumbai"];

// Required fields that must be non-empty before saving
const REQUIRED_FIELDS = [
    "emp_name", "emp_code", "pan_number", "location",
    "designation", "department", "bank_details", "doj",
    "effective_from", "effective_to", "company_address",
];

// PAN format used in Employee Center: 5 letters + 4 digits + 1 letter (all uppercase)
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

// Cap a single earning/deduction amount at ~100 crore. The DB allows DECIMAL(12,2)
// (≈9999 crore) so this is a sanity bound against typos like an extra digit.
const MAX_AMOUNT = 999999999.99;

// Max lengths - matched to the DB column widths so we never silently truncate on save.
// (pan_number is already capped at 10 by the transformValue helper.)
const MAX_LEN = {
    emp_name: 200,
    bank_details: 100,
    designation: 120,
    department: 150,
    location: 100,
    company_address: 500, // company_address is TEXT in DB - we pick a sensible UI cap
};

// Per-field input transform - force PAN to upper-case alphanumeric, max 10 chars
const transformValue = (key, value) => {
    if (key === "pan_number") {
        return String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
    }
    return value;
};

const EMPTY_INFO = {
    emp_id: "", effective_from: "", effective_to: "",
    emp_name: "", emp_code: "", pan_number: "", location: "",
    designation: "", department: "", bank_details: "", doj: "",
    note: "", company_address: "", font: DEFAULT_FONT_KEY,
};

// Month/Year helpers - UI stores effective_from as the FIRST day of the
// chosen From month and effective_to as the LAST day of the chosen To
// month, so the DB still holds plain DATE values.
const MONTH_LABELS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const firstOfMonth = (yr, monthIdx) => `${yr}-${String(monthIdx + 1).padStart(2, "0")}-01`;
const lastOfMonth = (yr, monthIdx) => {
    const d = new Date(yr, monthIdx + 1, 0);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const parseDateToYM = (s) => {
    if (!s) return { y: "", m: "" };
    const [y, m] = s.split("T")[0].split("-");
    return { y: parseInt(y), m: parseInt(m) - 1 };
};
const fmtMonthYear = (s) => {
    if (!s) return "-";
    const { y, m } = parseDateToYM(s);
    if (!y && y !== 0) return "-";
    return `${MONTH_LABELS[m]} ${y}`;
};
// Compact "Aug 2026" for the effective-range column.
const fmtMonthYearShort = (s) => {
    if (!s) return null;
    const { y, m } = parseDateToYM(s);
    if (!y && y !== 0) return null;
    return `${MONTH_LABELS[m].slice(0, 3)} ${y}`;
};

// ─── Main Component ───────────────────────────────────────────────────────────

function Payslip_Salary_Structure({ navSize }) {
    const navigate = useNavigate();

    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    // Sort - same option set + UX as Employee Center so the two pages match
    const SORT_OPTIONS = [
        { value: "emp_id_asc", label: "Employee Id A → Z" },
        { value: "emp_id_desc", label: "Employee Id Z → A" },
        { value: "name_asc", label: "Name A → Z" },
        { value: "name_desc", label: "Name Z → A" },
        { value: "doj_desc", label: "DOJ (newest first)" },
        { value: "doj_asc", label: "DOJ (oldest first)" },
        { value: "created_desc", label: "Recently added" },
        { value: "created_asc", label: "First added" },
    ];
    const [sortOrder, setSortOrder] = useState("emp_id_asc");
    const [showSortDropdown, setShowSortDropdown] = useState(false);

    // Status filter - quickly narrow to employees who already have a salary
    // structure vs those who don't.
    const [statusFilter, setStatusFilter] = useState("all"); // all | configured | not_configured
    // Pagination (mirrors Employee Center - Show 10/25/50/75/100 + page nav)
    const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 75, 100];
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [currentPage, setCurrentPage] = useState(1);

    // ── Templates + bulk-apply state ──
    const [templates, setTemplates] = useState([]);
    const [showTemplatesModal, setShowTemplatesModal] = useState(false);
    // Template editor - null = closed, {} = creating, {template_id,...} = editing
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [tplName, setTplName] = useState("");
    const [tplCompanyAddress, setTplCompanyAddress] = useState("");
    const [tplFont, setTplFont] = useState(DEFAULT_FONT_KEY);
    const [tplEarn, setTplEarn] = useState([]);
    const [tplDed, setTplDed] = useState([]);
    const [tplSaving, setTplSaving] = useState(false);
    // Bulk-apply
    const [selectedEmpIds, setSelectedEmpIds] = useState([]); // emp_id strings
    const [showBulkApplyModal, setShowBulkApplyModal] = useState(false);
    const [bulkTemplateId, setBulkTemplateId] = useState("");
    // Bulk apply is a two-stage flow: 1 = choose template/dates/employees,
    // 2 = review & edit each employee's populated components before saving.
    const [bulkStage, setBulkStage] = useState(1);
    // { emp_id: { earnings: [{component_id, amount}], deductions: [{component_id, amount}] } }
    const [bulkEmpItems, setBulkEmpItems] = useState({});
    // Bulk-apply info tooltip - rendered via a body portal (the modal's transform
    // traps position:fixed) and anchored to the hovered info icon, so it can't be
    // clipped by the modal's overflow.
    const [infoTip, setInfoTip] = useState(null);
    const showInfoTip = (el, t, earnItems, dedItems) => {
        const r = el.getBoundingClientRect();
        const below = r.bottom + 340 < window.innerHeight;
        setInfoTip({
            t, earnItems, dedItems,
            left: r.right + 8,          // open rightward from the icon (stays inside the modal)
            top: below ? r.bottom + 8 : r.top - 8,
            place: below ? "below" : "above",
        });
    };
    const [bulkEffectiveFrom, setBulkEffectiveFrom] = useState(new Date().toISOString().split("T")[0]);
    // Bulk apply now also captures Effective To (mandatory, mirrors the
    // single-emp SS modal). Default = a year from today on the same month boundary.
    const [bulkEffectiveTo, setBulkEffectiveTo] = useState(() => {
        const d = new Date();
        return lastOfMonth(d.getFullYear() + 1, d.getMonth());
    });
    const [bulkSaving, setBulkSaving] = useState(false);

    const fetchTemplates = async () => {
        try {
            const res = await apiFetch("/api/payslip/salary_structure/templates");
            if (!res.ok) {
                console.error("❌ templates fetch failed - HTTP", res.status, await res.text());
                setTemplates([]);
                return;
            }
            const data = await res.json();
            if (!Array.isArray(data)) {
                console.error("❌ templates response is not an array:", data);
                setTemplates([]);
                return;
            }
            setTemplates(data);
        } catch (err) {
            console.error("❌ templates fetch error:", err);
            setTemplates([]);
        }
    };

    // Component master (for the dropdowns)
    const [earnOptions, setEarnOptions] = useState([]);
    const [dedOptions, setDedOptions] = useState([]);

    // Department + Job-position lists (same source as Employee Center)
    const [deptNames, setDeptNames] = useState([]);
    const [jobNames, setJobNames] = useState([]);

    // Field-level validation errors → { fieldName: true }
    const [errors, setErrors] = useState({});

    // Panel
    const [panelOpen, setPanelOpen] = useState(false);
    const [panelEmp, setPanelEmp] = useState(null);
    const [info, setInfo] = useState({ ...EMPTY_INFO });
    const [earnings, setEarnings] = useState([]);   // [{ component_id, amount }]
    const [deductions, setDeductions] = useState([]); // [{ component_id, amount }]
    const [saving, setSaving] = useState(false);

    // ── Watermark anchoring (preview-only) ──
    // The watermark's TOP edge starts at the VERTICAL MIDDLE of the emp-details
    // block - the emp-info table on the structure preview, and the template-name
    // field (which sits in the same spot) on the template preview - so the mark
    // always begins at that block's half-height regardless of how many component
    // rows follow. Measured after layout. The generated PDF is untouched.
    // Nudge the mark up a little from the exact middle of the emp-details block
    // (kept identical to PayslipPreviewBody's WM_LIFT_PX so every preview matches).
    const SSP_WM_LIFT_PX = 30;
    const sspPaperRef = useRef(null);
    const sspInfoRef = useRef(null);
    const [sspWmTop, setSspWmTop] = useState(null);
    const tplPaperRef = useRef(null);
    const tplAnchorRef = useRef(null);
    const [tplWmTop, setTplWmTop] = useState(null);

    useLayoutEffect(() => {
        if (!panelOpen) return;
        const measure = () => {
            if (!sspPaperRef.current || !sspInfoRef.current) return;
            const c = sspPaperRef.current.getBoundingClientRect();
            const r = sspInfoRef.current.getBoundingClientRect();
            setSspWmTop(r.top - c.top + r.height / 2 - SSP_WM_LIFT_PX);
        };
        measure();
        window.addEventListener("resize", measure);
        // Re-measure on reflow (logo image load, font change, …) so the anchor
        // isn't fixed against a pre-image layout.
        let ro;
        if (typeof ResizeObserver !== "undefined" && sspPaperRef.current) {
            ro = new ResizeObserver(() => measure());
            ro.observe(sspPaperRef.current);
        }
        return () => {
            window.removeEventListener("resize", measure);
            if (ro) ro.disconnect();
        };
    }, [panelOpen, info.font]);

    useLayoutEffect(() => {
        if (!editingTemplate) return;
        const measure = () => {
            if (!tplPaperRef.current || !tplAnchorRef.current) return;
            const c = tplPaperRef.current.getBoundingClientRect();
            const r = tplAnchorRef.current.getBoundingClientRect();
            setTplWmTop(r.top - c.top + r.height / 2 - SSP_WM_LIFT_PX);
        };
        measure();
        window.addEventListener("resize", measure);
        let ro;
        if (typeof ResizeObserver !== "undefined" && tplPaperRef.current) {
            ro = new ResizeObserver(() => measure());
            ro.observe(tplPaperRef.current);
        }
        return () => {
            window.removeEventListener("resize", measure);
            if (ro) ro.disconnect();
        };
    }, [editingTemplate, tplFont]);

    // History / payslip-existence
    const [panelHistory, setPanelHistory] = useState([]);
    const [panelHasPayslips, setPanelHasPayslips] = useState(false);
    const [panelPayslipCount, setPanelPayslipCount] = useState(0);
    // Months (YYYY-MM) that already have a generated payslip - used to grey out
    // billed months in the From/To pickers (backdating + split guards).
    const [panelBilledMonths, setPanelBilledMonths] = useState([]);
    // Read-only "Pay-edit" virtual revisions surfaced from payslips whose
    // items differ from the underlying SS items for that pay month.
    const [panelPayslipOverrides, setPanelPayslipOverrides] = useState([]);
    // When viewing a payslip-edit virtual revision, we stash the override
    // record here so the form renders it instead of a real SS row.
    const [viewingOverrideId, setViewingOverrideId] = useState(null);
    const [viewingHistoryId, setViewingHistoryId] = useState(null);

    // ── Auth ──
    useEffect(() => {
        apiFetch("/api/employee/logined_employee", { method: "GET", credentials: "include" })
            .then((r) => r.json())
            .then((data) => {
                if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") {
                    navigate("/login");
                    return;
                }
                // Role redirect removed - ProtectedRoute (App.js) gates via matrix.
            })
            .catch(() => navigate("/login"));
    }, [navigate]);

    // ── Fetch employees + their latest structure summary ──
    const fetchAll = async () => {
        setLoading(true);
        try {
            const res = await apiFetch("/api/payslip/salary_structure/all");
            const data = await res.json();
            setEmployees(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("❌ fetchAll error:", err);
        } finally {
            setLoading(false);
        }
    };

    // ── Fetch component options (dropdowns) ──
    const fetchComponentOptions = async () => {
        try {
            const res = await apiFetch("/api/payslip/components");
            const data = await res.json();
            const list = Array.isArray(data) ? data : [];
            setEarnOptions(list.filter((c) => c.component_type === "Earning"));
            setDedOptions(list.filter((c) => c.component_type === "Deduction"));
        } catch (err) {
            console.error("❌ fetchComponentOptions error:", err);
        }
    };

    // ── Fetch department + job-position lists (same endpoints Employee Center uses) ──
    const fetchEmpConfigOptions = async () => {
        try {
            const [dRes, jRes] = await Promise.all([
                apiFetch("/api/Employee_configuration/get-department_config"),
                apiFetch("/api/Employee_configuration/get-job_position_config"),
            ]);
            const dData = await dRes.json();
            const jData = await jRes.json();
            setDeptNames(Array.isArray(dData) ? dData.map((x) => x.department_name).filter(Boolean) : []);
            setJobNames(Array.isArray(jData) ? jData.map((x) => x.job_name).filter(Boolean) : []);
        } catch (err) {
            console.error("❌ fetchEmpConfigOptions error:", err);
        }
    };

    useEffect(() => {
        fetchAll();
        fetchComponentOptions();
        fetchEmpConfigOptions();
        fetchTemplates();
    }, []);

    // Close sort dropdown on outside click
    useEffect(() => {
        if (!showSortDropdown) return;
        const handler = (e) => {
            if (!e.target.closest("[data-sort-dropdown]")) setShowSortDropdown(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [showSortDropdown]);

    // ── Split a structure's items into earnings/deductions rows ──
    const itemsToRows = (items, type) =>
        (items || [])
            .filter((it) => it.component_type === type)
            .map((it) => ({ component_id: String(it.component_id), amount: String(it.amount ?? "") }));

    // ── Load a structure (latest or historical) into the editor ──
    const loadStructureIntoForm = (s, isHistorical) => {
        // For a partially-billed revision the editable window starts AFTER the
        // last billed month (first_editable_from). The billed head stays on the
        // old revision; editing here splits it into two on save.
        const isPartBilled = s.billed_month_count > 0 && !s.fully_billed && s.first_editable_from;
        const effFrom = isPartBilled
            ? s.first_editable_from.split("T")[0]
            : (s.effective_from ? s.effective_from.split("T")[0] : new Date().toISOString().split("T")[0]);
        setInfo({
            emp_id: s.emp_id,
            effective_from: effFrom,
            effective_to: s.effective_to ? s.effective_to.split("T")[0] : "",
            emp_name: s.emp_name ?? "",
            emp_code: s.emp_code ?? "",
            pan_number: s.pan_number ?? "",
            location: s.location ?? "",
            designation: s.designation ?? "",
            department: s.department ?? "",
            bank_details: s.bank_details ?? "",
            doj: s.doj ?? "",
            note: s.note ?? "",
            company_address: s.company_address ?? "",
            font: s.font || DEFAULT_FONT_KEY,
        });
        setEarnings(itemsToRows(s.items, "Earning"));
        setDeductions(itemsToRows(s.items, "Deduction"));
        setViewingHistoryId(isHistorical ? s.structure_id : null);
        setViewingOverrideId(null);
    };

    // ── Open panel ──
    // opts.selectEffectiveFrom - when set (e.g. right after creating a new
    // revision), load THAT revision into the form instead of the default
    // current-in-effect one. A plain open (Edit button) passes nothing.
    const openPanel = async (emp, opts = {}) => {
        setPanelEmp(emp);
        setPanelOpen(true);
        setPanelHistory([]);
        setPanelHasPayslips(false);
        setPanelPayslipCount(0);
        setPanelPayslipOverrides([]);
        setViewingHistoryId(null);
        setViewingOverrideId(null);
        setErrors({});

        try {
            const res = await apiFetch(`/api/payslip/salary_structure/history/${emp.emp_id}`);
            const data = await res.json();
            const history = Array.isArray(data?.history) ? data.history : [];
            setPanelHistory(history);
            setPanelPayslipOverrides(Array.isArray(data?.payslip_overrides) ? data.payslip_overrides : []);
            setPanelHasPayslips(!!data?.has_payslips);
            setPanelPayslipCount(data?.payslip_count || 0);
            setPanelBilledMonths(Array.isArray(data?.billed_months) ? data.billed_months : []);

            if (history.length > 0) {
                // Prefer the revision that's CURRENTLY in effect for this month -
                // i.e. the most recent revision whose effective_from is <= today.
                // This way an upcoming/future-dated revision sitting on top of
                // the list doesn't hide what the employee is actually being paid
                // right now. Falls back to the latest row when no revision is
                // yet active (all future-dated) so the form still has something
                // to render. When the current rev isn't the top row of history,
                // mark it as "historical view" so loadedStructure / read-only /
                // banner logic resolves against the rev we actually loaded.
                // If the caller asked for a specific revision (just-created one),
                // select it; otherwise default to the current-in-effect revision.
                let revToLoad = null;
                if (opts.selectEffectiveFrom) {
                    const wanted = String(opts.selectEffectiveFrom).split("T")[0];
                    revToLoad = history.find((h) => (h.effective_from || "").split("T")[0] === wanted) || null;
                }
                if (!revToLoad) {
                    const todayStr = new Date().toISOString().split("T")[0];
                    const currentRev = history
                        .filter((h) => h.effective_from && h.effective_from <= todayStr)
                        .sort((a, b) => (a.effective_from < b.effective_from ? 1 : -1))[0];
                    revToLoad = currentRev || history[0];
                }
                const isNotLatest = revToLoad.structure_id !== history[0].structure_id;
                loadStructureIntoForm(revToLoad, isNotLatest);
            } else {
                // First structure ever - prefill emp-info from the employee record
                setInfo({
                    emp_id: emp.emp_id,
                    effective_from: new Date().toISOString().split("T")[0],
                    emp_name: `${emp.emp_first_name || ""} ${emp.emp_last_name || ""}`.trim(),
                    emp_code: emp.emp_id,
                    pan_number: emp.pan_number || "",
                    location: emp.emp_location || "",
                    designation: emp.job_position || "",
                    department: emp.emp_department || "",
                    bank_details: emp.bank_account_number || "",
                    doj: emp.joining_date || "",
                    note: DEFAULT_NOTE,
                    company_address: DEFAULT_ADDRESS,
                });
                setEarnings([{ component_id: "", amount: "" }]);
                setDeductions([]);
                setViewingHistoryId(null);
            }
        } catch (err) {
            console.error("❌ history fetch error:", err);
        }
    };

    const closePanel = () => {
        setPanelOpen(false);
        setPanelEmp(null);
        setInfo({ ...EMPTY_INFO });
        setEarnings([]);
        setDeductions([]);
        setPanelHistory([]);
        setPanelHasPayslips(false);
        setPanelPayslipCount(0);
        setPanelPayslipOverrides([]);
        setViewingHistoryId(null);
        setViewingOverrideId(null);
        setErrors({});
    };

    // Load a payslip-edit virtual revision into the form as READ-ONLY.
    // The user can see exactly what was edited on that month's payslip
    // but cannot modify it from here (edits go through Payslip Management).
    const loadOverrideIntoForm = (o) => {
        setInfo({
            emp_id: panelEmp?.emp_id || "",
            // Synthesize a from = first of pay month, to = last of pay month so
            // the title shows e.g. "Apr 2026 → Apr 2026"
            effective_from: firstOfMonth(o.pay_year, o.pay_month - 1),
            effective_to: lastOfMonth(o.pay_year, o.pay_month - 1),
            emp_name: panelEmp ? `${panelEmp.emp_first_name || ""} ${panelEmp.emp_last_name || ""}`.trim() : "",
            emp_code: panelEmp?.emp_id || "",
            pan_number: panelEmp?.pan_number || "",
            location: panelEmp?.emp_location || "",
            designation: panelEmp?.job_position || "",
            department: panelEmp?.emp_department || "",
            bank_details: panelEmp?.bank_account_number || "",
            doj: panelEmp?.joining_date || "",
            note: DEFAULT_NOTE,
            company_address: o.company_address || DEFAULT_ADDRESS,
        });
        setEarnings(itemsToRows(o.items, "Earning"));
        setDeductions(itemsToRows(o.items, "Deduction"));
        setViewingHistoryId(null);
        setViewingOverrideId(o.payslip_id);
    };

    const backToLatest = () => {
        // When admin is viewing a Pay-edit override, prefer jumping back to
        // the underlying revision that was active when that payslip was
        // generated (carried as based_on_structure_id) so the contrast
        // between "what the payslip became" and "what the SS says" is
        // immediately visible. Falls back to the latest row when the base
        // can't be resolved.
        if (viewingOverrideId !== null) {
            const ovr = panelPayslipOverrides.find((o) => o.payslip_id === viewingOverrideId);
            if (ovr && ovr.based_on_structure_id) {
                const base = panelHistory.find((h) => h.structure_id === ovr.based_on_structure_id);
                if (base) {
                    loadStructureIntoForm(base, base.structure_id !== panelHistory[0]?.structure_id);
                    return;
                }
            }
        }
        if (panelHistory[0]) loadStructureIntoForm(panelHistory[0], false);
    };

    // ── Which structure is currently loaded into the form ──
    //    viewingHistoryId is set when the user clicks Edit/View on a non-
    //    latest row. When it's null, the latest revision is loaded.
    const isViewingHistory = viewingHistoryId !== null;
    const loadedStructureId = viewingHistoryId ?? panelHistory[0]?.structure_id ?? null;
    const loadedStructure = panelHistory.find((h) => h.structure_id === loadedStructureId) || null;

    // Editability is now per-revision: the loaded structure is editable when
    // (a) the employee is active AND (b) no payslip has been generated against
    // this revision. That means OLD revisions can also be edited as long as
    // they haven't been used yet - matches what the user expects when nothing
    // has been billed against them.
    // Inputs-disabled flag - covers the cases where editing is truly forbidden:
    //   • inactive employee
    //   • viewing a Pay-edit override (read-only by design)
    // A "locked" revision (has_payslips_after_creation) is NOT included here
    // anymore. Admin should be able to TWEAK the values shown so they can be
    // forwarded into a "+ New Revision" with one click - saving in-place is
    // what's blocked, not the typing.
    const isPanelReadOnly =
        panelEmp?.active === 0 ||
        viewingOverrideId !== null;

    // ── Month-level billing state of the loaded revision ──────────────────
    //   • Fully locked  - every month billed → no edits, only "+ New Revision".
    //   • Partially billed - some months billed, some editable → editing SPLITS
    //     the revision (billed head keeps old values, edited tail becomes a new
    //     revision). Save button reads "Save Changes".
    //   • Unbilled - no month billed → plain in-place Save.
    const isRevisionFullyLocked =
        loadedStructure && loadedStructure.fully_billed;
    const isRevisionPartiallyBilled =
        loadedStructure && loadedStructure.billed_month_count > 0 && !loadedStructure.fully_billed;
    const isRevisionUnbilled =
        loadedStructure && loadedStructure.billed_month_count === 0;
    // Save button is hidden only when the revision is FULLY locked. The form
    // stays editable so a partial/locked revision's values can seed a split or
    // a "+ New Revision".
    const isRevisionLocked = isRevisionFullyLocked;

    // ── Date-based revision status - used to show "Current" / "Upcoming" /
    //    "Past" pills in the Revisions History table. The revision that's
    //    "currently in effect" is the one whose effective_from is the most
    //    recent date that's <= today (so a future-dated revision can sit on
    //    top without becoming "Current" prematurely).
    const today = new Date().toISOString().split("T")[0];
    const currentEffectiveDate = (panelHistory || [])
        .map((h) => h.effective_from)
        .filter((d) => d && d <= today)
        .sort()
        .pop() || null;
    const statusFor = (h) => {
        if (!h?.effective_from) return null;
        if (h.effective_from > today) return "Upcoming";
        if (h.effective_from === currentEffectiveDate) return "Current";
        return "Past";
    };

    // ── Row editors ──
    const setRow = (type, idx, patch) => {
        const setter = type === "Earning" ? setEarnings : setDeductions;
        setter((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    };
    const addRow = (type) => {
        const setter = type === "Earning" ? setEarnings : setDeductions;
        setter((rows) => [...rows, { component_id: "", amount: "" }]);
    };
    const removeRow = (type, idx) => {
        const setter = type === "Earning" ? setEarnings : setDeductions;
        setter((rows) => rows.filter((_, i) => i !== idx));
    };

    // Populate the Configure panel's earnings/deductions from a template. Admin
    // can then edit the amounts/components before saving (same as a fresh entry).
    const applyTemplateToPanel = (templateId) => {
        const t = templates.find((x) => String(x.template_id) === String(templateId));
        if (!t) return;
        setEarnings(itemsToRows(t.items, "Earning"));
        setDeductions(itemsToRows(t.items, "Deduction"));
    };

    // Options available to a given row (hide components already chosen in other rows of that section)
    const availableOptions = (allOptions, rows, currentIdx) => {
        const chosen = rows.map((r, i) => (i === currentIdx ? null : r.component_id)).filter(Boolean);
        return allOptions.filter((o) => !chosen.includes(String(o.component_id)));
    };

    // ── Totals ──
    const totalEarnings = earnings.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    const totalDeductions = deductions.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    const netPay = totalEarnings - totalDeductions;

    // ── Save ──
    const handleSave = async (asNewRevision = false, opts = {}) => {
        // opts.effectiveFromOverride lets the "+ New Revision" flow on a
        // locked revision pass a freshly-picked date without relying on the
        // form's effective_from (which still shows the locked revision's
        // original date). When set, this date is used in the payload AND
        // synced back into the form state so the UI matches what was saved.
        const effectiveFromForSave = opts.effectiveFromOverride || info.effective_from;
        // For new revisions the override flow passes "" explicitly so the
        // new revision starts open-ended regardless of the form's current To.
        const effectiveToForSave =
            opts.effectiveToOverride !== undefined ? opts.effectiveToOverride : info.effective_to;
        // Per-field validation - mark every empty required field with a red border
        const fieldErrors = {};
        const messages = [];

        REQUIRED_FIELDS.forEach((k) => {
            const v = info[k];
            if (v === undefined || v === null || String(v).trim() === "") {
                fieldErrors[k] = true;
            }
        });

        // PAN format - only check if non-empty (the empty case is already caught above)
        if (info.pan_number && !fieldErrors.pan_number && !PAN_REGEX.test(info.pan_number.trim())) {
            fieldErrors.pan_number = true;
            messages.push("Invalid PAN format - must be 5 letters + 4 digits + 1 letter (e.g. ABCDE1234F).");
        }

        const earnRows = earnings.filter((r) => r.component_id);
        const dedRows = deductions.filter((r) => r.component_id);
        if (earnRows.length === 0) {
            fieldErrors.earnings = true;
            messages.push("Add at least one Earning component.");
        }

        // Every Earning component must have an amount (0 is allowed; a blank /
        // non-numeric field is a half-finished entry and is still rejected).
        const earnBlank = earnRows.some((r) => r.amount === "" || r.amount == null || isNaN(parseFloat(r.amount)));
        if (earnBlank) {
            fieldErrors.earnings_zero = true;
            messages.push("Every Earning component must have an amount (0 or more).");
        }

        // Same rule for Deductions when any are added.
        const dedBlank = dedRows.some((r) => r.amount === "" || r.amount == null || isNaN(parseFloat(r.amount)));
        if (dedBlank) {
            fieldErrors.deductions_zero = true;
            messages.push("Every Deduction component must have an amount (0 or more).");
        }

        // Sanity cap on each amount - guards against accidental extra-digit typos
        const overflow = [...earnRows, ...dedRows].some((r) => {
            const n = parseFloat(r.amount);
            return !isNaN(n) && n > MAX_AMOUNT;
        });
        if (overflow) {
            fieldErrors.amount_overflow = true;
            messages.push(`Amount(s) exceed the maximum of ₹${MAX_AMOUNT.toLocaleString("en-IN")} per item.`);
        }

        // Net Pay must be strictly positive - block both zero and negative
        // (Total Earnings must exceed Total Deductions).
        if (!fieldErrors.earnings && !fieldErrors.earnings_zero && !fieldErrors.deductions_zero
            && earnRows.length > 0 && netPay <= 0) {
            fieldErrors.netpay_zero = true;
            messages.push("Net Pay must be greater than zero - Total Earnings must exceed Total Deductions.");
        }

        setErrors(fieldErrors);

        if (Object.keys(fieldErrors).length) {
            // Build the message: format-specific lines first, then the generic "fill all" line if anything was empty
            const COMPUTED_KEYS = ["pan_number", "earnings", "earnings_zero", "deductions_zero", "amount_overflow", "netpay_zero"];
            const hasEmpty = Object.keys(fieldErrors).some((k) => !COMPUTED_KEYS.includes(k));
            const lines = [...messages];
            if (hasEmpty) lines.unshift("Please fill all highlighted fields.");
            // One error per line - .dn-toast-text is white-space: pre-line.
            dnAlert.warning("Required", lines.join("\n"));
            return;
        }

        // Split-on-save: editing a partially-billed revision. The billed head
        // keeps its old values; the edited tail becomes a brand-new revision.
        const isSplitSave = !asNewRevision && loadedStructure
            && loadedStructure.billed_month_count > 0 && !loadedStructure.fully_billed;
        if (isSplitSave) {
            const lbLabel = `${MONTH_LABELS[loadedStructure.last_billed_month - 1]} ${loadedStructure.last_billed_year}`;
            const tailFrom = fmtMonthYear(effectiveFromForSave);
            const tailTo = effectiveToForSave ? fmtMonthYear(effectiveToForSave) : "open-ended";
            const ok = await dnAlert.confirm({
                title: "Save as a new revision?",
                message: `Payslips already exist up to ${lbLabel}, so those months stay on the current structure. Your changes will be saved as a NEW revision effective ${tailFrom} → ${tailTo}.`,
                confirmLabel: "Yes, create new revision",
            });
            if (!ok) return;
        }

        setSaving(true);
        try {
            const items = [
                ...earnRows.map((r) => ({ component_id: r.component_id, amount: parseFloat(r.amount) || 0 })),
                ...dedRows.map((r) => ({ component_id: r.component_id, amount: parseFloat(r.amount) || 0 })),
            ];

            const payload = {
                emp_id: info.emp_id,
                effective_from: effectiveFromForSave,
                effective_to: effectiveToForSave || null,
                emp_name: info.emp_name, emp_code: info.emp_code, pan_number: info.pan_number,
                location: info.location, designation: info.designation, department: info.department,
                bank_details: info.bank_details, doj: info.doj,
                note: DEFAULT_NOTE, company_address: info.company_address,
                font: info.font || DEFAULT_FONT_KEY,
                items,
            };
            if (asNewRevision) {
                payload.as_new_revision = true;
            } else if (isSplitSave) {
                // Partially-billed revision → backend shortens the billed head
                // and inserts the edited tail as a new revision.
                payload.split_structure_id = loadedStructure.structure_id;
            } else if (loadedStructure && loadedStructure.billed_month_count === 0) {
                // In-place save against an unbilled revision (latest OR an older
                // unused one). Backend identifies the target by structure_id.
                payload.structure_id = loadedStructure.structure_id;
            }

            const res = await apiFetch("/api/payslip/salary_structure/save", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (data.success) {
                dnAlert.success("Saved!");
                fetchAll();
                // Keep the editor modal open and refresh its data in place. After
                // creating a NEW revision (or a split), select that new revision so
                // the admin lands on what they just created; a plain in-place edit
                // reloads the current revision as before. Re-opening the modal
                // fresh (Edit button) still defaults to the current revision.
                if (panelEmp) {
                    const selectFrom = (asNewRevision || isSplitSave) ? effectiveFromForSave : null;
                    openPanel(panelEmp, selectFrom ? { selectEffectiveFrom: selectFrom } : {});
                }
            } else {
                dnAlert.error("Error", data.error || "Could not save structure.");
            }
        } catch {
            dnAlert.error("Error", "Network error. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    // "+ New Revision" click - ALWAYS prompt for a fresh effective date
    // (whether the current revision is locked or not). Using whatever was
    // in the Effective From input by default is a footgun: locked revisions
    // bounce off the sequential check, and unlocked ones quietly create a
    // new revision with the same old date which the user almost never
    // actually wants. One consistent flow is clearer than two.
    const handleNewRevisionClick = async () => {
        // Default suggestion: From = next month, To = 12 months later.
        // Admin can adjust either selector before confirming.
        const today = new Date();
        const fromY = today.getMonth() === 11 ? today.getFullYear() + 1 : today.getFullYear();
        const fromM = (today.getMonth() + 1) % 12; // 0-indexed next month
        const toY = fromY + 1;
        const toM = fromM;
        // Full 2018-2050 window: a salary revision can legitimately be dated
        // years ahead, and old revisions still need to be reachable.
        const yearOpts = dslipYears({ desc: true });

        // Set of "YYYY-MM" strings already covered by an existing revision.
        // Building this from panelHistory lets us grey out taken months in
        // the month <select>s so admin can't pick an overlapping date range.
        const takenSet = new Set();
        const addMonth = (y, m) => takenSet.add(`${y}-${String(m + 1).padStart(2, "0")}`);
        (panelHistory || []).forEach((h) => {
            if (!h.effective_from || !h.effective_to) return;
            const f = parseDateToYM(h.effective_from);
            const t = parseDateToYM(h.effective_to);
            if (f.y === "" || t.y === "") return;
            let y = f.y, m = f.m;
            while (y < t.y || (y === t.y && m <= t.m)) {
                addMonth(y, m);
                m++;
                if (m > 11) { m = 0; y++; }
            }
        });
        // Billed months (already have a payslip) are off-limits for a new
        // revision too - same "YYYY-MM" format as addMonth above.
        (panelBilledMonths || []).forEach((ym) => takenSet.add(ym));
        const isTaken = (y, m) => takenSet.has(`${y}-${String(m + 1).padStart(2, "0")}`);

        const yearOptsHtml = yearOpts.map((y) => `<option value="${y}">${y}</option>`).join("");
        // Build month options for a given year - taken months show as
        // "Month (taken)" with the disabled attribute set so they can't
        // be picked. Selected param flags which one starts highlighted.
        const monthOptsHtmlFor = (yr, selected) => MONTH_LABELS.map((label, i) => {
            const taken = isTaken(yr, i);
            const dis = taken ? " disabled" : "";
            const sel = i === selected ? " selected" : "";
            const lbl = taken ? `${label} (taken)` : label;
            return `<option value="${i}"${dis}${sel}>${lbl}</option>`;
        }).join("");

        const { value: picked, isConfirmed } = await Swal.fire({
            title: "New Revision - Effective From → To",
            html: `
                <p class="ssp-newrev-copy">
                    A new revision will be created with the current emp-info, components, and amounts - but with the date range you pick below.
                </p>
                <p class="ssp-newrev-copy">
                    Pick a <strong>From</strong> month after the last generated payslip and a <strong>To</strong> month when this revision should stop applying. Months already covered by another revision are disabled.
                </p>
                <div class="ssp-newrev-range-row">
                    <label class="ssp-newrev-date-label">From</label>
                    <select id="ssp-newrev-fm" class="ssp-newrev-sel">${monthOptsHtmlFor(fromY, fromM)}</select>
                    <select id="ssp-newrev-fy" class="ssp-newrev-sel">${yearOptsHtml}</select>
                </div>
                <div class="ssp-newrev-range-row">
                    <label class="ssp-newrev-date-label">To</label>
                    <select id="ssp-newrev-tm" class="ssp-newrev-sel">${monthOptsHtmlFor(toY, toM)}</select>
                    <select id="ssp-newrev-ty" class="ssp-newrev-sel">${yearOptsHtml}</select>
                </div>
            `,
            confirmButtonText: "Create New Revision",
            confirmButtonColor: "#059669",
            showCancelButton: true,
            cancelButtonText: "Cancel",
            // Closes only via Create / Cancel - a stray backdrop click must not
            // discard the picked date range.
            allowOutsideClick: false,
            customClass: {
                popup: "ssp-newrev-popup",
                confirmButton: "ssp-newrev-confirm",
                cancelButton: "ssp-newrev-cancel",
            },
            didOpen: () => {
                const fmSel = document.getElementById("ssp-newrev-fm");
                const fySel = document.getElementById("ssp-newrev-fy");
                const tmSel = document.getElementById("ssp-newrev-tm");
                const tySel = document.getElementById("ssp-newrev-ty");
                fySel.value = String(fromY);
                tySel.value = String(toY);

                // Rebuild the month list for whichever year is now selected
                // so disabled months stay in sync as the admin flips years.
                const rebuildMonths = (monthSel, yearSel) => {
                    const y = parseInt(yearSel.value);
                    const currentVal = parseInt(monthSel.value);
                    const keep = !isNaN(currentVal) && !isTaken(y, currentVal) ? currentVal : -1;
                    monthSel.innerHTML = monthOptsHtmlFor(y, keep);
                    if (keep === -1) {
                        // Fall back to first un-taken month so we don't leave a
                        // disabled option selected.
                        for (let m = 0; m < 12; m++) {
                            if (!isTaken(y, m)) { monthSel.value = String(m); break; }
                        }
                    }
                };
                fySel.addEventListener("change", () => rebuildMonths(fmSel, fySel));
                tySel.addEventListener("change", () => rebuildMonths(tmSel, tySel));
            },
            preConfirm: () => {
                const fm = parseInt(document.getElementById("ssp-newrev-fm")?.value);
                const fy = parseInt(document.getElementById("ssp-newrev-fy")?.value);
                const tm = parseInt(document.getElementById("ssp-newrev-tm")?.value);
                const ty = parseInt(document.getElementById("ssp-newrev-ty")?.value);
                if ([fm, fy, tm, ty].some((v) => isNaN(v))) {
                    Swal.showValidationMessage("Pick both From and To month + year");
                    return false;
                }
                const fromDate = firstOfMonth(fy, fm);
                const toDate = lastOfMonth(ty, tm);
                if (toDate < fromDate) {
                    Swal.showValidationMessage("To must be on or after From");
                    return false;
                }
                // Final guard: walk the picked range and reject if ANY month
                // inside it overlaps an existing revision. Backend re-checks,
                // but failing fast here gives a clearer message.
                let y = fy, m = fm;
                while (y < ty || (y === ty && m <= tm)) {
                    if (isTaken(y, m)) {
                        Swal.showValidationMessage(
                            `${MONTH_LABELS[m]} ${y} is already covered by another revision.`
                        );
                        return false;
                    }
                    m++;
                    if (m > 11) { m = 0; y++; }
                }
                return { fromDate, toDate };
            },
        });

        if (!isConfirmed || !picked) return;

        setInfo((f) => ({ ...f, effective_from: picked.fromDate, effective_to: picked.toDate }));
        await handleSave(true, { effectiveFromOverride: picked.fromDate, effectiveToOverride: picked.toDate });
    };

    // ── Delete the CURRENTLY LOADED unused revision ──
    //    Works for any unused revision (latest or older), not just the
    //    very last one. Used revisions never reach this path because the
    //    Delete button is hidden when isPanelReadOnly.
    const handleDelete = async () => {
        const target = loadedStructure;
        if (!target) return;
        const ok = await dnAlert.confirm({
            title: "Delete this salary structure?",
            message: `Effective ${target.effective_from}. This cannot be undone.`,
            danger: true,
            confirmLabel: "Yes, delete",
        });
        if (!ok) return;
        try {
            const res = await apiFetch(`/api/payslip/salary_structure/delete/${target.structure_id}`, { method: "DELETE" });
            const data = await res.json();
            if (data.success) {
                dnAlert.success("Deleted");
                closePanel();
                fetchAll();
            } else {
                dnAlert.error("Error", data.error || "Could not delete.");
            }
        } catch {
            dnAlert.error("Error", "Network error. Please try again.");
        }
    };

    // ─────────── Template handlers ───────────
    const openCreateTemplate = () => {
        setEditingTemplate({});
        setTplName("");
        setTplCompanyAddress(DEFAULT_ADDRESS); // sensible pre-fill so admin doesn't have to retype it every time
        setTplFont(DEFAULT_FONT_KEY);
        setTplEarn([{ component_id: "", amount: "" }]);
        setTplDed([]);
    };
    const openEditTemplate = (t) => {
        setEditingTemplate(t);
        setTplName(t.template_name);
        setTplCompanyAddress(t.company_address || DEFAULT_ADDRESS);
        setTplFont(t.font || DEFAULT_FONT_KEY);
        setTplEarn(
            (t.items || [])
                .filter((i) => i.component_type === "Earning")
                .map((i) => ({ component_id: String(i.component_id), amount: String(i.amount ?? "") }))
        );
        setTplDed(
            (t.items || [])
                .filter((i) => i.component_type === "Deduction")
                .map((i) => ({ component_id: String(i.component_id), amount: String(i.amount ?? "") }))
        );
    };
    const closeTemplateEditor = () => {
        setEditingTemplate(null);
        setTplName(""); setTplCompanyAddress(""); setTplFont(DEFAULT_FONT_KEY); setTplEarn([]); setTplDed([]);
    };

    const handleSaveTemplate = async () => {
        if (!tplName.trim()) { dnAlert.warning("Required", "Template name is required."); return; }
        const cleanedEarn = tplEarn.filter((r) => r.component_id);
        const cleanedDed = tplDed.filter((r) => r.component_id);
        if (!cleanedEarn.length) { dnAlert.warning("Required", "Add at least one Earning component."); return; }
        // A blank amount is treated as 0 for templates (all-zero values are
        // allowed here) - the payload coerces empty → 0 below.
        const tplNet = cleanedEarn.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0)
                     - cleanedDed.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
        // Templates may have a Net Pay of 0 (all-zero component values are allowed
        // here); only a NEGATIVE net pay is rejected. The per-employee salary
        // structure still requires Net Pay > 0.
        if (tplNet < 0) { dnAlert.warning("Net Pay cannot be negative", "Total Deductions cannot exceed Total Earnings."); return; }

        setTplSaving(true);
        try {
            const items = [
                ...cleanedEarn.map((r) => ({ component_id: r.component_id, amount: parseFloat(r.amount) || 0 })),
                ...cleanedDed.map((r) => ({ component_id: r.component_id, amount: parseFloat(r.amount) || 0 })),
            ];
            const url = editingTemplate?.template_id
                ? `/api/payslip/salary_structure/template/${editingTemplate.template_id}`
                : `/api/payslip/salary_structure/template`;
            const method = editingTemplate?.template_id ? "PUT" : "POST";
            const res = await apiFetch(url, {
                method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    template_name: tplName.trim(),
                    company_address: tplCompanyAddress.trim() || null,
                    font: tplFont || DEFAULT_FONT_KEY,
                    items,
                }),
            });
            const data = await res.json();
            if (data.success) {
                dnAlert.success(editingTemplate?.template_id ? "Updated" : "Created");
                fetchTemplates();
                // Keep the editor open (data updated in place) instead of
                // closing. On a fresh create, flip into edit mode of the new
                // template so the next save updates it, not create a duplicate.
                if (!editingTemplate?.template_id && data.template_id) {
                    setEditingTemplate({ template_id: data.template_id });
                }
            } else {
                dnAlert.error("Error", data.error || "Could not save template.");
            }
        } catch {
            dnAlert.error("Error", "Network error.");
        } finally {
            setTplSaving(false);
        }
    };

    const handleDeleteTemplate = async (t) => {
        const ok = await dnAlert.confirm({
            title: "Delete this template?",
            message: `${t.template_name} will be removed. Existing salary structures that were applied from this template are not affected.`,
            danger: true,
            confirmLabel: "Delete",
        });
        if (!ok) return;
        try {
            const res = await apiFetch(`/api/payslip/salary_structure/template/${t.template_id}`, { method: "DELETE" });
            const data = await res.json();
            if (data.success) {
                dnAlert.success("Deleted");
                fetchTemplates();
            } else {
                dnAlert.error("Error", data.error || "Could not delete.");
            }
        } catch {
            dnAlert.error("Error", "Network error.");
        }
    };

    // ─────────── Bulk-apply handlers ───────────
    const toggleEmpSelected = (emp_id) => {
        setSelectedEmpIds((prev) =>
            prev.includes(emp_id) ? prev.filter((x) => x !== emp_id) : [...prev, emp_id]
        );
    };
    const clearSelection = () => setSelectedEmpIds([]);
    const selectAllVisibleNotConfigured = () => {
        const ids = paged.filter((e) => !e.structure && e.active !== 0).map((e) => e.emp_id);
        setSelectedEmpIds(ids);
    };

    const openBulkApply = () => {
        if (!templates.length) {
            dnAlert.info("No templates", "Create a template first via Manage Templates.");
            return;
        }
        setBulkTemplateId(String(templates[0].template_id));
        setBulkStage(1);
        setBulkEmpItems({});
        // Default to current month first day → 12 months later last day.
        const today = new Date();
        setBulkEffectiveFrom(firstOfMonth(today.getFullYear(), today.getMonth()));
        setBulkEffectiveTo(lastOfMonth(today.getFullYear() + 1, today.getMonth()));
        setShowBulkApplyModal(true);
    };

    // ── Per-employee item editing in stage 2 ──────────────────────
    const setBulkRow = (empId, type, idx, patch) => {
        setBulkEmpItems((prev) => {
            const key = type === "Earning" ? "earnings" : "deductions";
            const list = prev[empId]?.[key] || [];
            return { ...prev, [empId]: { ...prev[empId], [key]: list.map((r, i) => (i === idx ? { ...r, ...patch } : r)) } };
        });
    };
    const addBulkRow = (empId, type) => {
        setBulkEmpItems((prev) => {
            const key = type === "Earning" ? "earnings" : "deductions";
            const list = prev[empId]?.[key] || [];
            return { ...prev, [empId]: { ...prev[empId], [key]: [...list, { component_id: "", amount: "" }] } };
        });
    };
    const removeBulkRow = (empId, type, idx) => {
        setBulkEmpItems((prev) => {
            const key = type === "Earning" ? "earnings" : "deductions";
            const list = prev[empId]?.[key] || [];
            return { ...prev, [empId]: { ...prev[empId], [key]: list.filter((_, i) => i !== idx) } };
        });
    };
    const bulkEmpNet = (empId) => {
        const it = bulkEmpItems[empId] || { earnings: [], deductions: [] };
        const g = it.earnings.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
        const d = it.deductions.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
        return g - d;
    };

    // Stage 1 → 2: validate the choices, then populate each selected employee's
    // components from the chosen template so they can be edited before saving.
    const handleBulkNext = () => {
        if (!bulkTemplateId) { dnAlert.warning("Required", "Pick a template."); return; }
        if (!bulkEffectiveFrom) { dnAlert.warning("Required", "Pick an effective-from date."); return; }
        if (!bulkEffectiveTo) { dnAlert.warning("Required", "Pick an effective-to date."); return; }
        if (bulkEffectiveTo < bulkEffectiveFrom) {
            dnAlert.warning("Invalid range", "Effective To must be on or after Effective From.");
            return;
        }
        if (!selectedEmpIds.length) { dnAlert.warning("Required", "Select at least one employee."); return; }

        const tpl = templates.find((t) => String(t.template_id) === String(bulkTemplateId));
        const tplItems = tpl?.items || [];
        const mkRows = (type) => tplItems
            .filter((i) => i.component_type === type)
            .map((i) => ({ component_id: String(i.component_id), amount: String(i.amount ?? "") }));
        const init = {};
        selectedEmpIds.forEach((id) => { init[id] = { earnings: mkRows("Earning"), deductions: mkRows("Deduction") }; });
        setBulkEmpItems(init);
        setBulkStage(2);
    };

    // Stage 2: validate every employee's edited items, then save. A template
    // may have Net Pay 0, but each employee's saved structure must be > 0.
    const handleBulkApply = async () => {
        for (const id of selectedEmpIds) {
            const it = bulkEmpItems[id] || { earnings: [], deductions: [] };
            const earnRows = it.earnings.filter((r) => r.component_id);
            const dedRows = it.deductions.filter((r) => r.component_id);
            const emp = employees.find((e) => e.emp_id === id);
            const who = emp ? `${emp.emp_first_name} ${emp.emp_last_name}`.trim() || id : id;
            if (earnRows.length === 0) { dnAlert.warning("Add an earning", `${who}: add at least one Earning component.`); return; }
            // Blank amounts are treated as 0; the Net Pay > 0 gate below ensures
            // the structure is still valid.
            if (bulkEmpNet(id) <= 0) {
                dnAlert.warning("Net Pay must be greater than zero", `${who}: Total Earnings must exceed Total Deductions.`); return;
            }
        }

        setBulkSaving(true);
        try {
            const payloadEmployees = selectedEmpIds.map((id) => {
                const it = bulkEmpItems[id] || { earnings: [], deductions: [] };
                const items = [...it.earnings, ...it.deductions]
                    .filter((r) => r.component_id)
                    .map((r) => ({ component_id: parseInt(r.component_id), amount: parseFloat(r.amount) || 0 }));
                return { emp_id: id, items };
            });
            const res = await apiFetch("/api/payslip/salary_structure/bulk_apply", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    template_id: parseInt(bulkTemplateId),
                    employees: payloadEmployees,
                    effective_from: bulkEffectiveFrom,
                    effective_to: bulkEffectiveTo,
                }),
            });
            const data = await res.json();

            // All employees passed validation → all structures created.
            if (data.success) {
                dnAlert.success("All applied", `${data.created_count} structure(s) created.`);
                setShowBulkApplyModal(false);
                clearSelection();
                fetchAll();
                return;
            }

            // Atomic abort - at least one employee failed validation, so
            // the backend created NOTHING. Show the full skip table and
            // keep the modal + selection intact so the user can fix the
            // underlying data and re-apply without re-selecting.
            if (data.aborted && Array.isArray(data.skipped) && data.skipped.length) {
                const rows = data.skipped.map((s, i) => `
                    <tr style="background:${i % 2 === 0 ? "#ffffff" : "#fef2f2"};border-bottom:1px solid #fecaca;">
                        <td style="padding:8px 12px;font-weight:600;font-family:'Courier New',monospace;font-size:12px;">${escapeHtml(s.emp_id)}</td>
                        <td style="padding:8px 12px;color:#7f1d1d;font-size:12px;line-height:1.4;">${escapeHtml(s.reason)}</td>
                    </tr>
                `).join("");
                await dnAlert.report({
                    title: "Bulk apply aborted",
                    wide: true,
                    danger: true,
                    html: `
                        <div style="text-align:left;font-size:13px;color:#374151;line-height:1.5;margin-bottom:14px;">
                            ${escapeHtml(data.error)}
                        </div>
                        <div style="border:1px solid #fecaca;border-radius:8px;overflow:hidden;">
                            <table style="width:100%;border-collapse:collapse;font-size:13px;font-family:'Segoe UI',sans-serif;">
                                <thead>
                                    <tr style="background:#dc2626;color:#fff;">
                                        <th style="padding:10px 12px;text-align:left;">Employee</th>
                                        <th style="padding:10px 12px;text-align:left;">Reason</th>
                                    </tr>
                                </thead>
                                <tbody>${rows}</tbody>
                            </table>
                        </div>
                        <div style="text-align:left;font-size:12px;color:#6b7280;margin-top:12px;">
                            No structures were created. Fix every issue in Employee Center, then re-apply.
                        </div>`,
                });
                // intentionally keep modal open + selection intact
                return;
            }

            // Generic error path
            dnAlert.error("Error", data.error || "Could not apply.");
        } catch {
            dnAlert.error("Error", "Network error.");
        } finally {
            setBulkSaving(false);
        }
    };

    // ── Filtered + sorted list ──
    const filtered = employees.filter((e) => {
        // Status filter - configured = has a salary_structure attached
        if (statusFilter === "configured" && !e.structure) return false;
        if (statusFilter === "not_configured" && e.structure) return false;
        const q = search.toLowerCase();
        return (
            e.emp_id.toLowerCase().includes(q) ||
            `${e.emp_first_name} ${e.emp_last_name}`.toLowerCase().includes(q) ||
            (e.emp_department || "").toLowerCase().includes(q)
        );
    });

    // Pre-computed counts for the chip labels (uses raw employees so the count
    // doesn't shrink as you narrow the search)
    const configuredCount = employees.filter((e) => e.structure).length;
    const notConfiguredCount = employees.length - configuredCount;

    // Mirrors Employee_Center's sort: same option labels + sort fns so the two
    // pages feel identical to the user.
    const sortEmployees = (list) => {
        const sorted = [...list];
        switch (sortOrder) {
            case "emp_id_asc": return sorted.sort((a, b) => (a.emp_id || "").localeCompare(b.emp_id || "", undefined, { numeric: true }));
            case "emp_id_desc": return sorted.sort((a, b) => (b.emp_id || "").localeCompare(a.emp_id || "", undefined, { numeric: true }));
            case "name_asc": return sorted.sort((a, b) => (a.emp_first_name || "").localeCompare(b.emp_first_name || ""));
            case "name_desc": return sorted.sort((a, b) => (b.emp_first_name || "").localeCompare(a.emp_first_name || ""));
            case "doj_desc": return sorted.sort((a, b) => new Date(b.joining_date || 0) - new Date(a.joining_date || 0));
            case "doj_asc": return sorted.sort((a, b) => new Date(a.joining_date || 0) - new Date(b.joining_date || 0));
            case "created_desc": return sorted.sort((a, b) => new Date(b.created_time || 0) - new Date(a.created_time || 0));
            case "created_asc": return sorted.sort((a, b) => new Date(a.created_time || 0) - new Date(b.created_time || 0));
            default: return sorted;
        }
    };
    const visible = sortEmployees(filtered);

    // ── Pagination ──
    const totalPages = Math.max(1, Math.ceil(visible.length / rowsPerPage));
    const paged = visible.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
    // Reset to page 1 whenever the filter/search/sort/page-size changes so we
    // never sit on a page that no longer exists.
    useEffect(() => { setCurrentPage(1); }, [search, statusFilter, sortOrder, rowsPerPage]);

    const latestUnused = panelHistory[0] && !panelHistory[0].has_payslips_after_creation;

    // Clear a single field's error when the user starts editing it
    const clearError = (key) => {
        if (errors[key]) setErrors((er) => { const n = { ...er }; delete n[key]; return n; });
    };

    // ── Single emp-info input cell (no label, no row wrapper) ──
    const infoCell = (key, type) => (
        <input
            type={type || "text"}
            className={`ssp-info-input ${errors[key] ? "ssp-input-error" : ""}`}
            value={info[key] ?? ""}
            disabled={isPanelReadOnly}
            readOnly={key === "emp_code"}
            maxLength={key === "pan_number" ? 10 : MAX_LEN[key]}
            onClick={type === "date" ? (e) => e.target.showPicker?.() : undefined}
            onChange={(e) => {
                const v = transformValue(key, e.target.value);
                setInfo((f) => ({ ...f, [key]: v }));
                clearError(key);
            }}
        />
    );

    // ── Searchable dropdown cell (LOCATION, DESIGNATION, DEPARTMENT) ──
    const selectCell = (key, options) => {
        const opts = options.map((o) => ({ value: o, label: o }));
        // Keep legacy/unknown values visible so existing data doesn't vanish
        if (info[key] && !options.includes(info[key])) {
            opts.unshift({ value: info[key], label: info[key] });
        }
        const current = opts.find((o) => o.value === info[key]) || null;
        return (
            <Select
                classNamePrefix="ssp-rs"
                options={opts}
                value={current}
                isDisabled={isPanelReadOnly}
                isSearchable
                isClearable
                placeholder="- select -"
                styles={cellSelectStyles(!!errors[key])}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPlacement="auto"
                maxMenuHeight={220}
                closeMenuOnScroll={(e) => !!(e && e.target && e.target.classList && e.target.classList.contains("ssp-body"))}
                onChange={(opt) => {
                    setInfo((f) => ({ ...f, [key]: opt ? opt.value : "" }));
                    clearError(key);
                }}
            />
        );
    };

    const disabledCell = () => <span className="ssp-info-disabled">- set at generation -</span>;

    // ── One paid-table half (used twice per row: earnings half + deductions half) ──
    const renderPayCell = (type, rows, idx, options) => {
        const row = rows[idx];
        if (!row) {
            return (<><div className="ssp-pay-cell" /><div className="ssp-pay-cell" /></>);
        }
        // Build options list, hiding components already chosen elsewhere in this column
        const available = availableOptions(options, rows, idx);
        const compOpts = available.map((o) => ({ value: String(o.component_id), label: o.component_name }));
        // Ensure the row's current component is still in the list (even if filtered out elsewhere)
        if (row.component_id && !compOpts.find((o) => o.value === String(row.component_id))) {
            const c = options.find((o) => String(o.component_id) === String(row.component_id));
            if (c) compOpts.unshift({ value: String(c.component_id), label: c.component_name });
        }
        const current = compOpts.find((o) => o.value === String(row.component_id)) || null;

        return (
            <>
                <div className="ssp-pay-cell ssp-pay-cell-select">
                    <div className="ssp-pay-rs-wrap">
                        <Select
                            classNamePrefix="ssp-rs"
                            options={compOpts}
                            value={current}
                            isDisabled={isPanelReadOnly}
                            isSearchable
                            isClearable
                            placeholder="- select -"
                            styles={cellSelectStyles(false)}
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            menuPlacement="auto"
                            maxMenuHeight={220}
                            closeMenuOnScroll={(e) => !!(e && e.target && e.target.classList && e.target.classList.contains("ssp-body"))}
                            onChange={(opt) => setRow(type, idx, { component_id: opt ? opt.value : "" })}
                        />
                    </div>
                    {!isPanelReadOnly && (
                        <button className="ssp-pay-remove" title="Remove" onClick={() => removeRow(type, idx)}>
                            <FaTrash />
                        </button>
                    )}
                </div>
                <div className="ssp-pay-cell ssp-pay-cell-amount">
                    <input
                        type="number"
                        placeholder="0"
                        min="0"
                        value={row.amount}
                        disabled={isPanelReadOnly}
                        onWheel={(e) => e.target.blur()}
                        onKeyDown={(e) => {
                            // Block scientific-notation 'e/E', and sign characters
                            if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
                        }}
                        onPaste={(e) => {
                            // Block paste of any non-numeric chars (allow digits + single '.')
                            const txt = (e.clipboardData || window.clipboardData).getData("text");
                            if (!/^\d*\.?\d*$/.test(txt)) e.preventDefault();
                        }}
                        onChange={(e) => {
                            let v = e.target.value;
                            if (v) {
                                // Strip leading zeros "0123" → "123" but ONLY if the
                                // first non-zero char is a digit. Without the
                                // [1-9] guard, deleting a digit from the middle of
                                // "30000.00" can leave "0000.00", which the old
                                // regex collapsed to ".00" - destroying the value
                                // every time the user backspaces.
                                v = v.replace(/^0+(?=[1-9])/, "");
                                // Cap decimal places to 2 (DB column is DECIMAL(12,2))
                                const m = v.match(/^(\d*)(?:\.(\d{0,2}))?/);
                                if (m) v = m[1] + (m[2] !== undefined ? "." + m[2] : "");
                            }
                            setRow(type, idx, { amount: v });
                        }}
                    />
                </div>
            </>
        );
    };

    const maxRows = Math.max(earnings.length, deductions.length);

    return (
        <div className="pss-container">
            <div className={`pss-dashboard ${navSize}`}>
                <div className="pss-content">

                    {/* Page header */}
                    <div className="pss-page-header">
                        <div className="pss-page-header-left">
                            <h2><a className="navbar-brand" href="#">
                                <img src={logo_eagle} alt="dAdmin Logo" style={{ height: "50px", objectFit: "contain", marginLeft: "-20px", marginRight: "10px" }} />
                            </a>Salary Structure</h2>
                            <p className="pss-subtitle">
                                Configure each employee's payslip - earnings, deductions and details. Built from your Payslip Components.
                            </p>
                        </div>
                    </div>

                    {/* Search */}
                    <div className="pss-toolbar">
                        <div className="pss-search-wrapper">
                            <FaSearch className="pss-search-icon" />
                            <input
                                type="text"
                                className="pss-search-input"
                                placeholder="Search by name, ID or department…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>

                        {/* Status filter chips */}
                        <div className="pss-status-filter">
                            {[
                                { v: "all", label: "All", count: employees.length },
                                { v: "configured", label: "Configured", count: configuredCount },
                                { v: "not_configured", label: "Not configured", count: notConfiguredCount },
                            ].map((opt) => (
                                <button
                                    key={opt.v}
                                    type="button"
                                    onClick={() => setStatusFilter(opt.v)}
                                    className={`pss-status-chip ${statusFilter === opt.v ? `pss-status-chip--active pss-status-chip--${opt.v}` : ""}`}
                                >
                                    {opt.label}
                                    <span className="pss-status-chip-count">{opt.count}</span>
                                </button>
                            ))}
                        </div>

                        {/* Sort dropdown - same pattern as Employee Center */}
                        <div style={{ position: "relative" }} data-sort-dropdown>
                            <button
                                onClick={() => setShowSortDropdown((p) => !p)}
                                style={{
                                    display: "flex", alignItems: "center", gap: 6,
                                    padding: "6px 12px", borderRadius: 6, border: "1px solid #d1d5db",
                                    background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 500,
                                    color: "#374151", whiteSpace: "nowrap",
                                    boxShadow: showSortDropdown ? "0 0 0 2px #f97316aa" : "none",
                                    outline: showSortDropdown ? "2px solid #f9731655" : "none",
                                }}
                            >
                                <span style={{ fontSize: 15 }}>&#8645;</span>
                                {SORT_OPTIONS.find((o) => o.value === sortOrder)?.label || "Sort by…"}
                                <span style={{ fontSize: 10, marginLeft: 2 }}>▼</span>
                            </button>
                            {showSortDropdown && (
                                <div style={{
                                    position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 999,
                                    background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
                                    boxShadow: "0 4px 16px rgba(0,0,0,0.12)", minWidth: 200, overflow: "hidden",
                                }}>
                                    <div style={{ padding: "8px 12px", fontSize: 11, fontWeight: 600, color: "#9ca3af", borderBottom: "1px solid #f3f4f6", letterSpacing: "0.05em" }}>
                                        &#8645; Sort by…
                                    </div>
                                    {SORT_OPTIONS.map((opt) => (
                                        <div
                                            key={opt.value}
                                            onClick={() => { setSortOrder(opt.value); setShowSortDropdown(false); }}
                                            style={{
                                                padding: "9px 16px", fontSize: 13, cursor: "pointer",
                                                background: sortOrder === opt.value ? "#1d4ed8" : "transparent",
                                                color: sortOrder === opt.value ? "#fff" : "#374151",
                                                fontWeight: sortOrder === opt.value ? 600 : 400,
                                                transition: "background 0.15s",
                                            }}
                                            onMouseEnter={(e) => { if (sortOrder !== opt.value) e.currentTarget.style.background = "#f3f4f6"; }}
                                            onMouseLeave={(e) => { if (sortOrder !== opt.value) e.currentTarget.style.background = "transparent"; }}
                                        >
                                            {opt.label}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <span className="pss-count-badge">
                            {filtered.length} employee{filtered.length !== 1 ? "s" : ""}
                        </span>

                        <div className="pss-rows-per-page">
                            <span className="pss-rows-per-page-label">Show</span>
                            <ScrollSelect
                                value={rowsPerPage}
                                options={ROWS_PER_PAGE_OPTIONS.map((n) => ({ value: n, label: String(n) }))}
                                minWidth={72}
                                onChange={(v) => { setRowsPerPage(Number(v)); setCurrentPage(1); }}
                            />
                        </div>

                        <button
                            type="button"
                            className="pss-templates-btn"
                            onClick={() => setShowTemplatesModal(true)}
                            title="Create and manage reusable salary-structure templates"
                        >
                            📋 Manage Templates
                        </button>
                    </div>

                    {/* Bulk action bar - sticky bar that appears when employees are selected */}
                    {selectedEmpIds.length > 0 && (
                        <div className="pss-bulk-bar">
                            <div className="pss-bulk-info">
                                <strong>{selectedEmpIds.length}</strong> employee{selectedEmpIds.length !== 1 ? "s" : ""} selected
                            </div>
                            <div className="pss-bulk-actions">
                                <button className="pss-bulk-btn pss-bulk-btn--ghost" onClick={clearSelection}>Clear</button>
                                <button className="pss-bulk-btn pss-bulk-btn--apply" onClick={openBulkApply}>
                                    ⚡ Apply Template
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Table */}
                    <div className="pss-card">
                        {loading ? (
                            <div className="pss-loading">Loading salary structures…</div>
                        ) : (
                            <div className="pss-table-wrapper">
                                <table className="pss-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 38 }}>
                                                {(() => {
                                                    const eligibleIds = paged.filter((e) => !e.structure && e.active !== 0).map((e) => e.emp_id);
                                                    const allSelected = eligibleIds.length > 0 && eligibleIds.every((id) => selectedEmpIds.includes(id));
                                                    return (
                                                        <input
                                                            type="checkbox"
                                                            checked={allSelected}
                                                            onChange={() => allSelected ? clearSelection() : selectAllVisibleNotConfigured()}
                                                            title="Select all visible un-configured employees"
                                                            disabled={eligibleIds.length === 0}
                                                            style={{ cursor: eligibleIds.length === 0 ? "not-allowed" : "pointer" }}
                                                        />
                                                    );
                                                })()}
                                            </th>
                                            <th>Employee</th>
                                            <th>Gross</th>
                                            <th>Deductions</th>
                                            <th>Net Pay</th>
                                            <th>Valid</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visible.length === 0 ? (
                                            <tr><td colSpan={8} className="pss-empty">No employees found.</td></tr>
                                        ) : (
                                            paged.map((emp) => {
                                                const s = emp.structure;
                                                const canSelect = !s && emp.active !== 0;
                                                return (
                                                    <tr key={emp.emp_id} className={selectedEmpIds.includes(emp.emp_id) ? "pss-row-selected" : ""}>
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedEmpIds.includes(emp.emp_id)}
                                                                onChange={() => toggleEmpSelected(emp.emp_id)}
                                                                disabled={!canSelect}
                                                                title={!canSelect ? (s ? "Already has a structure" : "Inactive employee") : "Select for bulk apply"}
                                                                style={{ cursor: canSelect ? "pointer" : "not-allowed" }}
                                                            />
                                                        </td>
                                                        <td>
                                                            <div className="pss-emp-cell">
                                                                <div className="pss-avatar">{emp.emp_first_name?.[0]?.toUpperCase()}</div>
                                                                <div>
                                                                    <div className="pss-emp-name">{emp.emp_first_name} {emp.emp_last_name}</div>
                                                                    <div className="pss-emp-meta">
                                                                        <span className="pss-emp-id">{emp.emp_id}</span>
                                                                        <span className="pss-emp-dept-sep">·</span>
                                                                        <span className="pss-emp-dept">{emp.emp_department || "-"}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="pss-num">{s ? `₹${fmt(s.gross)}` : "-"}</td>
                                                        <td className="pss-num">{s ? `₹${fmt(s.total_deductions)}` : "-"}</td>
                                                        <td className="pss-num">{s ? `₹${fmt(s.net_pay)}` : "-"}</td>
                                                        <td className="pss-eff">
                                                            {s ? (
                                                                <>
                                                                    <span className="pss-eff-from">{fmtMonthYearShort(s.effective_from) || "-"}</span>
                                                                    <span className="pss-eff-arrow"> → </span>
                                                                    {s.effective_to
                                                                        ? <span className="pss-eff-to">{fmtMonthYearShort(s.effective_to)}</span>
                                                                        : <span className="pss-eff-open">open</span>}
                                                                </>
                                                            ) : "-"}
                                                        </td>
                                                        <td>
                                                            {emp.active === 0 ? (
                                                                <span className="pss-pill pill-grey">Inactive</span>
                                                            ) : (
                                                                <span className={`pss-pill ${s ? "pill-green" : "pill-orange"}`}>
                                                                    {s ? "Active" : "Not Configured"}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {emp.active === 0 ? (
                                                                <button className="pss-action-btn btn-view" onClick={() => openPanel(emp)} disabled={!s}
                                                                    title={s ? "View only - employee is inactive" : "No structure to view"}
                                                                    style={!s ? { opacity: 0.5, cursor: "not-allowed" } : {}}>
                                                                    View
                                                                </button>
                                                            ) : (
                                                                <button className={`pss-action-btn ${s ? "btn-edit" : "btn-configure"}`} onClick={() => openPanel(emp)}>
                                                                    {s ? "Edit" : "Configure"}
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Pagination footer */}
                    {!loading && visible.length > 0 && (
                        <div className="pss-pagination">
                            <span className="pss-pagination-info">
                                Showing {(currentPage - 1) * rowsPerPage + 1}–{Math.min(currentPage * rowsPerPage, visible.length)} of {visible.length}
                            </span>
                            <div className="pss-pagination-nav">
                                <button
                                    className="pss-pagination-btn"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                >
                                    Previous
                                </button>
                                <span className="pss-pagination-page">Page {currentPage} of {totalPages}</span>
                                <button
                                    className="pss-pagination-btn"
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ══ PAYSLIP-STYLE EDITOR MODAL ══ */}
            {panelOpen && panelEmp && (
                <>
                    <div className="ssp-overlay" />
                    <div className="ssp-modal">
                        {/* Modal top bar */}
                        <div className="ssp-modal-top">
                            <div>
                                <div className="ssp-modal-title">
                                    {isViewingHistory
                                        ? (isPanelReadOnly
                                            ? `Viewing Revision (Effective ${fmtMonthYear(info.effective_from)}${info.effective_to ? ` → ${fmtMonthYear(info.effective_to)}` : ""})`
                                            : `Editing Revision (Effective ${fmtMonthYear(info.effective_from)}${info.effective_to ? ` → ${fmtMonthYear(info.effective_to)}` : ""})`)
                                        : isPanelReadOnly
                                            ? "View Salary Structure"
                                            : (panelHistory[0] ? "Edit" : "Configure") + " Salary Structure"}
                                </div>
                                <div className="ssp-modal-sub">
                                    {panelEmp.emp_first_name} {panelEmp.emp_last_name} · {panelEmp.emp_id}
                                    {panelEmp?.active === 0 && <span className="pss-pill pill-grey" style={{ marginLeft: 10, fontSize: 11 }}>Inactive</span>}
                                </div>
                            </div>
                            <button className="ssp-close" onClick={closePanel}><FaTimes /></button>
                        </div>

                        {/* Scrollable payslip body */}
                        <div className="ssp-body">
                            {/* Locked-revision banner - fires when the loaded structure
                                CAN'T be edited because payslip(s) have already been
                                generated against it. Without this banner the user
                                just sees disabled inputs with no explanation. */}
                            {panelEmp?.active !== 0 && isRevisionFullyLocked ? (
                                <div className="ssp-warn ssp-warn--locked">
                                    <strong>⚠ This revision is fully locked.</strong>
                                    &nbsp;Every month in this revision already has a generated payslip. You can still tweak the values below - they'll be carried forward when you create a <strong>+ New Revision</strong>{isViewingHistory ? <> (click <strong>← Back to Latest</strong> first)</> : null}.
                                </div>
                            ) : panelEmp?.active !== 0 && isRevisionPartiallyBilled ? (
                                <div className="ssp-warn ssp-warn--locked">
                                    <strong>ℹ Editing from {fmtMonthYear(info.effective_from)} onward.</strong>
                                    &nbsp;Payslips already exist up to <strong>{MONTH_LABELS[loadedStructure.last_billed_month - 1]} {loadedStructure.last_billed_year}</strong>, so those months stay on the current structure. Change any value and click <strong>Save Changes</strong> to save the remaining months as a new revision.
                                </div>
                            ) : null}

                            {/* Inactive-employee banner - also locks the form */}
                            {panelEmp?.active === 0 ? (
                                <div className="ssp-warn ssp-warn--inactive">
                                    <strong>⚠ This employee is inactive.</strong>
                                    &nbsp;The salary structure is shown for reference only and can't be edited or revised.
                                </div>
                            ) : null}

                            <div className="ssp-paper" style={fontCssStyle(info.font)} ref={sspPaperRef}>
                                {/* Full DOLLUZ CORP logo watermark - matches the generated PDF / preview.
                                    Top edge anchored to the LOCATION row (see sspWmTop). */}
                                <div
                                    className="ssp-watermark ssp-watermark--sharp"
                                    style={{ backgroundImage: `url(${DOLLUZ_Full_Logo})`, ...(sspWmTop != null ? { top: sspWmTop } : {}) }}
                                />
                                <div
                                    className="ssp-watermark ssp-watermark--blur"
                                    style={{ backgroundImage: `url(${DOLLUZ_Full_Logo})`, ...(sspWmTop != null ? { top: sspWmTop } : {}) }}
                                />

                                {/* gold accent + full logo + title (HARDCODED) */}
                                <div className="ssp-gold" />
                                <div className="ssp-logo-wrap">
                                    <img src={DOLLUZ_Full_Logo} alt="Dolluz Corp" className="ssp-logo" />
                                </div>
                                <div className="ssp-title">PAYSLIP FOR THE MONTH - <span className="ssp-month-ph">[MONTH YEAR - set at generation]</span></div>

                                {/* Emp-info table - 4-column bordered grid, matches the PDF */}
                                <div className="ssp-info-table" ref={sspInfoRef}>
                                    <div className="ssp-info-row">
                                        <div className="ssp-info-cell ssp-info-label">NAME:</div>
                                        <div className="ssp-info-cell ssp-info-value">{infoCell("emp_name")}</div>
                                        <div className="ssp-info-cell ssp-info-label">PAN:</div>
                                        <div className="ssp-info-cell ssp-info-value">{infoCell("pan_number")}</div>
                                    </div>
                                    <div className="ssp-info-row">
                                        <div className="ssp-info-cell ssp-info-label">EMPLOYEE CODE:</div>
                                        <div className="ssp-info-cell ssp-info-value">{infoCell("emp_code")}</div>
                                        <div className="ssp-info-cell ssp-info-label">LOCATION:</div>
                                        <div className="ssp-info-cell ssp-info-value">{selectCell("location", LOCATIONS)}</div>
                                    </div>
                                    <div className="ssp-info-row">
                                        <div className="ssp-info-cell ssp-info-label">DESIGNATION:</div>
                                        <div className="ssp-info-cell ssp-info-value">{selectCell("designation", jobNames)}</div>
                                        <div className="ssp-info-cell ssp-info-label">BANK DETAILS:</div>
                                        <div className="ssp-info-cell ssp-info-value">{infoCell("bank_details")}</div>
                                    </div>
                                    <div className="ssp-info-row">
                                        <div className="ssp-info-cell ssp-info-label">DEPARTMENT:</div>
                                        <div className="ssp-info-cell ssp-info-value">{selectCell("department", deptNames)}</div>
                                        <div className="ssp-info-cell ssp-info-label">DAYS PAYABLE:</div>
                                        <div className="ssp-info-cell ssp-info-value">{disabledCell()}</div>
                                    </div>
                                    <div className="ssp-info-row">
                                        <div className="ssp-info-cell ssp-info-label">DOJ:</div>
                                        <div className="ssp-info-cell ssp-info-value">{infoCell("doj", "date")}</div>
                                        <div className="ssp-info-cell ssp-info-label">LOP:</div>
                                        <div className="ssp-info-cell ssp-info-value">{disabledCell()}</div>
                                    </div>
                                </div>

                                {/* Apply a template to pre-fill the components (editable after) */}
                                {!isPanelReadOnly && templates.length > 0 && (
                                    <div className="ssp-panel-tpl">
                                        <span className="ssp-panel-tpl-label">Start from a template</span>
                                        <div className="ssp-panel-tpl-sel">
                                            <Select
                                                classNamePrefix="ssp-rs"
                                                options={templates.map((t) => ({ value: String(t.template_id), label: `${t.template_name}  ·  Net ₹${fmt(t.net_pay)}` }))}
                                                value={null}
                                                isSearchable
                                                placeholder="Apply a template…"
                                                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                                menuPlacement="auto"
                                                maxMenuHeight={220}
                                                styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
                                                onChange={(opt) => { if (opt) applyTemplateToPanel(opt.value); }}
                                            />
                                        </div>
                                        {(earnings.length > 0 || deductions.length > 0) && (
                                            <span className="ssp-panel-tpl-hint">Replaces the rows below - edit before saving.</span>
                                        )}
                                    </div>
                                )}

                                {/* Single 4-column bordered table - Earnings + Deductions zipped */}
                                <div className="ssp-pay-table">
                                    <div className="ssp-pay-row ssp-pay-head">
                                        <div className="ssp-pay-cell">Earnings</div>
                                        <div className="ssp-pay-cell ssp-pay-right">Monthly in INR</div>
                                        <div className="ssp-pay-cell">Deductions</div>
                                        <div className="ssp-pay-cell ssp-pay-right">Monthly in INR</div>
                                    </div>
                                    {Array.from({ length: maxRows }).map((_, i) => (
                                        <div className="ssp-pay-row" key={i}>
                                            {renderPayCell("Earning", earnings, i, earnOptions)}
                                            {renderPayCell("Deduction", deductions, i, dedOptions)}
                                        </div>
                                    ))}
                                    {!isPanelReadOnly && (
                                        <div className="ssp-pay-row ssp-pay-addrow">
                                            <div className="ssp-pay-cell ssp-pay-cell-add">
                                                <button onClick={() => addRow("Earning")}><FaPlus /> Add earning</button>
                                            </div>
                                            <div className="ssp-pay-cell" />
                                            <div className="ssp-pay-cell ssp-pay-cell-add">
                                                <button onClick={() => addRow("Deduction")}><FaPlus /> Add deduction</button>
                                            </div>
                                            <div className="ssp-pay-cell" />
                                        </div>
                                    )}
                                    <div className="ssp-pay-row ssp-pay-totals">
                                        <div className="ssp-pay-cell">Total Earnings</div>
                                        <div className="ssp-pay-cell ssp-pay-right">{fmt(totalEarnings)}</div>
                                        <div className="ssp-pay-cell">Total Deductions</div>
                                        <div className="ssp-pay-cell ssp-pay-right">{fmt(totalDeductions)}</div>
                                    </div>
                                    <div className="ssp-pay-row ssp-pay-netpay">
                                        <div className="ssp-pay-cell" />
                                        <div className="ssp-pay-cell" />
                                        <div className="ssp-pay-cell">NET PAY</div>
                                        <div className="ssp-pay-cell ssp-pay-right">{fmt(netPay)}</div>
                                    </div>
                                </div>

                                {/* Note (HARDCODED) */}
                                <div className="ssp-note-static">
                                    <strong>Note:</strong> {DEFAULT_NOTE}
                                </div>

                                {/* Payslip font - picks the typeface used for both
                                    the PDF generator and the HTML preview modals.
                                    One of 5 presets baked into the shared registry. */}
                                <div className="ssp-field-block ssp-font-block">
                                    <label htmlFor="ssp-font-sel">Payslip Font</label>
                                    <select
                                        id="ssp-font-sel"
                                        className="ssp-font-sel"
                                        value={info.font || DEFAULT_FONT_KEY}
                                        disabled={isPanelReadOnly}
                                        onChange={(e) => setInfo((f) => ({ ...f, font: e.target.value }))}
                                        style={fontCssStyle(info.font)}
                                    >
                                        {PAYSLIP_FONTS.map((f) => (
                                            <option key={f.key} value={f.key} style={fontCssStyle(f.key)}>
                                                {f.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Company address (DYNAMIC) + signature (HARDCODED) */}
                                <div className="ssp-footer-grid">
                                    <div className="ssp-field-block">
                                        <label>Company Address</label>
                                        <textarea
                                            className={`ssp-textarea ${errors.company_address ? "ssp-input-error" : ""}`}
                                            rows={3}
                                            value={info.company_address}
                                            disabled={isPanelReadOnly}
                                            maxLength={MAX_LEN.company_address}
                                            onChange={(e) => {
                                                setInfo((f) => ({ ...f, company_address: e.target.value }));
                                                clearError("company_address");
                                            }}
                                        />
                                        <div className="ssp-char-count">
                                            {(info.company_address || "").length} / {MAX_LEN.company_address}
                                        </div>
                                    </div>
                                    <div className="ssp-sign">
                                        <img src={signature_img} alt="Signature" className="ssp-sign-img" />
                                        <div className="ssp-sign-label">AUTHORIZED SIGNATURE</div>
                                    </div>
                                </div>
                            </div>

                            {/* Revisions history - real salary-structure revisions.
                                Status is date-based (Current / Upcoming / Past). */}
                            {panelHistory.length > 1 && (
                                <div className="ssp-revisions">
                                    <div className="ssp-revisions-title">Revisions history</div>
                                    <table className="ssp-rev-table">
                                        <thead>
                                            <tr>
                                                <th>Effective From</th>
                                                <th>Effective To</th>
                                                <th>Gross</th>
                                                <th>Net Pay</th>
                                                <th>Status</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {panelHistory.map((h) => {
                                                const status = statusFor(h);
                                                const isLoaded = loadedStructureId === h.structure_id && viewingOverrideId === null;
                                                // Partially-billed revisions are still editable (editing splits
                                                // them); only a fully-billed revision is view-only.
                                                const isEditable = panelEmp?.active !== 0 && !h.fully_billed;
                                                const pillClass =
                                                    status === "Current" ? "pill-green" :
                                                    status === "Upcoming" ? "pill-amber" :
                                                    "pill-slate";
                                                return (
                                                    <tr key={h.structure_id} style={{ background: isLoaded ? "#fff8e1" : "transparent" }}>
                                                        <td>{fmtMonthYear(h.effective_from)}</td>
                                                        <td>{h.effective_to ? fmtMonthYear(h.effective_to) : <span style={{ color: "#9ca3af" }}>open-ended</span>}</td>
                                                        <td>₹{fmt(h.gross)}</td>
                                                        <td>₹{fmt(h.net_pay)}</td>
                                                        <td>
                                                            <span className={`pss-pill ${pillClass}`} style={{ fontSize: 11 }}>{status}</span>
                                                        </td>
                                                        <td>
                                                            {isLoaded ? (
                                                                <span className="pss-pill pill-grey" style={{ fontSize: 11 }}>
                                                                    {isEditable ? "Editing" : "Viewing"}
                                                                </span>
                                                            ) : (
                                                                <button
                                                                    className={`pss-action-btn ${isEditable ? "btn-edit" : "btn-view"}`}
                                                                    style={{ padding: "3px 10px", fontSize: 12 }}
                                                                    onClick={() => loadStructureIntoForm(h, h.structure_id !== panelHistory[0]?.structure_id)}
                                                                >
                                                                    {isEditable ? "Edit" : "View"}
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Payslip edits - read-only month-level overrides
                                made through Payslip Generate / Payslip Management.
                                Kept in their own section so the real revisions
                                stay visually clean. */}
                            {panelPayslipOverrides.length > 0 && (
                                <div className="ssp-revisions ssp-revisions--edits">
                                    <div className="ssp-revisions-title">
                                        Payslip Edits
                                        <span className="ssp-revisions-sub">
                                            &nbsp;- months where the payslip differs from the salary structure
                                        </span>
                                    </div>
                                    <table className="ssp-rev-table">
                                        <thead>
                                            <tr>
                                                <th>Pay Month</th>
                                                <th>Gross</th>
                                                <th>Net Pay</th>
                                                <th>Status</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {panelPayslipOverrides.map((o) => {
                                                const isLoaded = viewingOverrideId === o.payslip_id;
                                                const monthLabel = fmtMonthYear(firstOfMonth(o.pay_year, o.pay_month - 1));
                                                return (
                                                    <tr
                                                        key={`ovr-${o.payslip_id}`}
                                                        style={{ background: isLoaded ? "#fff8e1" : "transparent" }}
                                                    >
                                                        <td>{monthLabel}</td>
                                                        <td>₹{fmt(o.gross)}</td>
                                                        <td>₹{fmt(o.net_pay)}</td>
                                                        <td>
                                                            <span className="pss-pill pill-blue" style={{ fontSize: 11 }}>Pay-edit</span>
                                                        </td>
                                                        <td>
                                                            {isLoaded ? (
                                                                <span className="pss-pill pill-grey" style={{ fontSize: 11 }}>Viewing</span>
                                                            ) : (
                                                                <button
                                                                    className="pss-action-btn btn-view"
                                                                    style={{ padding: "3px 10px", fontSize: 12 }}
                                                                    onClick={() => loadOverrideIntoForm(o)}
                                                                    title="Read-only view - edit through Payslip Management"
                                                                >View</button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Footer actions - Effective From/To uses month+year
                            selectors. From-month becomes first-of-month;
                            To-month becomes LAST-of-month at save time. Leave
                            To blank for open-ended (still in force). */}
                        <div className="ssp-actions">
                            <div className="ssp-actions-eff ssp-actions-eff--range">
                                <label>Effective From - To</label>
                                {(() => {
                                    const fromYM = parseDateToYM(info.effective_from);
                                    const toYM = parseDateToYM(info.effective_to);
                                    // Full 2018-2050 window, same as the New Revision dialog.
                                    const yearsAll = dslipYears({ desc: true });
                                    // Fallback year used when a month is picked before a year.
                                    const currentY = new Date().getFullYear();
                                    // Months already covered by OTHER revisions for this employee -
                                    // the currently-loaded revision's own months stay enabled so
                                    // admin can fine-tune the range it already owns.
                                    const takenSetInline = new Set();
                                    (panelHistory || []).forEach((h) => {
                                        if (loadedStructureId && h.structure_id === loadedStructureId) return;
                                        if (!h.effective_from || !h.effective_to) return;
                                        const f = parseDateToYM(h.effective_from);
                                        const t = parseDateToYM(h.effective_to);
                                        if (f.y === "" || t.y === "") return;
                                        let yy = f.y, mm = f.m;
                                        while (yy < t.y || (yy === t.y && mm <= t.m)) {
                                            takenSetInline.add(`${yy}-${String(mm + 1).padStart(2, "0")}`);
                                            mm++;
                                            if (mm > 11) { mm = 0; yy++; }
                                        }
                                    });
                                    const isTakenInline = (yy, mm) =>
                                        takenSetInline.has(`${yy}-${String(mm + 1).padStart(2, "0")}`);
                                    // Months that already have a generated payslip - can't be
                                    // covered by any new / backdated / split revision.
                                    const billedSetInline = new Set(panelBilledMonths);
                                    const isBilledInline = (yy, mm) =>
                                        billedSetInline.has(`${yy}-${String(mm + 1).padStart(2, "0")}`);
                                    return (
                                        <div className="ssp-range-row">
                                            <Select
                                                styles={rangeSelectStyles(MONTH_SEL_W, !!errors.effective_from)}
                                                {...rangeSelectProps("top")}
                                                isDisabled={isPanelReadOnly}
                                                placeholder="From month"
                                                value={fromYM.m === "" ? null : { value: fromYM.m, label: MONTH_LABELS[fromYM.m] }}
                                                options={MONTH_LABELS.map((label, i) => {
                                                    const yr = fromYM.y || currentY;
                                                    const taken = isTakenInline(yr, i) || isBilledInline(yr, i);
                                                    return { value: i, label: taken ? `${label} (taken)` : label, isDisabled: taken };
                                                })}
                                                onChange={(opt) => {
                                                    const y = fromYM.y || currentY;
                                                    setInfo((f) => ({ ...f, effective_from: firstOfMonth(y, opt.value) }));
                                                    clearError("effective_from");
                                                }}
                                            />
                                            <Select
                                                styles={rangeSelectStyles(YEAR_SEL_W, !!errors.effective_from)}
                                                {...DSLIP_YEAR_SELECT}
                                                {...rangeSelectProps("top")}
                                                isDisabled={isPanelReadOnly}
                                                placeholder="Year"
                                                value={fromYM.y ? { value: fromYM.y, label: String(fromYM.y) } : null}
                                                options={yearsAll.map((y) => ({ value: y, label: String(y) }))}
                                                onChange={(opt) => {
                                                    const m = fromYM.m === "" ? 0 : fromYM.m;
                                                    setInfo((f) => ({ ...f, effective_from: firstOfMonth(opt.value, m) }));
                                                    clearError("effective_from");
                                                }}
                                            />
                                            <span className="ssp-range-sep">→</span>
                                            <Select
                                                styles={rangeSelectStyles(MONTH_SEL_W, !!errors.effective_to)}
                                                {...rangeSelectProps("top")}
                                                isDisabled={isPanelReadOnly}
                                                placeholder="To month"
                                                value={toYM.m === "" ? null : { value: toYM.m, label: MONTH_LABELS[toYM.m] }}
                                                options={MONTH_LABELS.map((label, i) => {
                                                    const yr = toYM.y || fromYM.y || currentY;
                                                    const taken = isTakenInline(yr, i) || isBilledInline(yr, i);
                                                    return { value: i, label: taken ? `${label} (taken)` : label, isDisabled: taken };
                                                })}
                                                onChange={(opt) => {
                                                    const y = toYM.y || fromYM.y || currentY;
                                                    setInfo((f) => ({ ...f, effective_to: lastOfMonth(y, opt.value) }));
                                                    clearError("effective_to");
                                                }}
                                            />
                                            <Select
                                                styles={rangeSelectStyles(YEAR_SEL_W, !!errors.effective_to)}
                                                {...DSLIP_YEAR_SELECT}
                                                {...rangeSelectProps("top")}
                                                isDisabled={isPanelReadOnly || toYM.m === ""}
                                                placeholder="Year"
                                                value={toYM.y ? { value: toYM.y, label: String(toYM.y) } : null}
                                                options={yearsAll.map((y) => ({ value: y, label: String(y) }))}
                                                onChange={(opt) => {
                                                    const m = toYM.m === "" ? 0 : toYM.m;
                                                    setInfo((f) => ({ ...f, effective_to: lastOfMonth(opt.value, m) }));
                                                    clearError("effective_to");
                                                }}
                                            />
                                        </div>
                                    );
                                })()}
                                {panelEmp?.joining_date && (
                                    <span className="ssp-hint">earliest: {fmtMonthYear(panelEmp.joining_date)}</span>
                                )}
                            </div>
                            <div className="ssp-actions-btns">
                                <button className="pss-btn-cancel" onClick={closePanel} disabled={saving}>
                                    {isPanelReadOnly ? "Close" : "Cancel"}
                                </button>
                                {/* Delete: any unused loaded revision (latest OR older).
                                    Locked revisions can't be deleted either, since a
                                    payslip already references them. */}
                                {!isPanelReadOnly && isRevisionUnbilled && panelEmp?.active !== 0 && loadedStructure && (
                                    <button className="pss-btn-save" style={{ background: "#dc2626" }} onClick={handleDelete} disabled={saving}>Delete</button>
                                )}
                                {/* Back to Latest / Back to Salary Structure:
                                    shown when viewing an older revision OR a
                                    Pay-edit override so admin can hop back to
                                    the canonical structure without closing. */}
                                {(isViewingHistory || viewingOverrideId !== null) && (
                                    <button className="pss-btn-save" style={{ background: "#6b7280" }} onClick={backToLatest}>
                                        {viewingOverrideId !== null ? "← Back to Salary Structure" : "← Back to Latest"}
                                    </button>
                                )}
                                {/* Save: in-place update against whichever revision is loaded.
                                    Hidden for locked revisions - the admin's edits are meant
                                    to feed into "+ New Revision" instead of overwriting a
                                    revision a payslip has already been generated against. */}
                                {!isPanelReadOnly && !isRevisionFullyLocked && (
                                    <button className="pss-btn-save" onClick={() => handleSave(false)} disabled={saving}>
                                        {saving ? "Saving…" : (isRevisionPartiallyBilled ? "Save Changes" : "Save")}
                                    </button>
                                )}
                                {/* + New Revision: always available for an active employee that
                                    already has a structure - the new-revision date picker is
                                    independent of whichever revision is loaded, so it works the
                                    same whether you're on the current, an older, or the latest
                                    revision. Hidden only in the read-only Pay-edit override view
                                    (click "Back to Salary Structure" first). */}
                                {panelEmp?.active !== 0 && panelHistory[0] && viewingOverrideId === null && (
                                    <button className="pss-btn-save" style={{ background: "#059669" }} onClick={handleNewRevisionClick} disabled={saving}>
                                        + New Revision
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ══ MANAGE TEMPLATES MODAL (list view) ══ */}
            {showTemplatesModal && (
                <>
                    <div className="ssp-overlay" />
                    <div className="ssp-modal tpl-modal">
                        <div className="ssp-modal-top">
                            <div>
                                <div className="ssp-modal-title">Salary structure templates</div>
                                <div className="ssp-modal-sub">Reusable templates for bulk-configuring employees</div>
                            </div>
                            <button className="ssp-close" onClick={() => setShowTemplatesModal(false)}><FaTimes /></button>
                        </div>
                        <div className="ssp-body tpl-body">
                            <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ fontSize: 13, color: "#6b7280" }}>
                                    {templates.length} template{templates.length !== 1 ? "s" : ""}
                                </span>
                                <button className="tpl-new-btn" onClick={openCreateTemplate}>
                                    + New Template
                                </button>
                            </div>
                            {templates.length === 0 ? (
                                <div className="tpl-empty">
                                    No templates yet. Create one to bulk-configure multiple employees with the same earnings/deductions.
                                </div>
                            ) : (
                                <table className="tpl-table">
                                    <thead>
                                        <tr>
                                            <th>Template</th>
                                            <th>Earnings</th>
                                            <th>Deductions</th>
                                            <th>Gross</th>
                                            <th>Net</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {templates.map((t) => {
                                            const earnCount = (t.items || []).filter((i) => i.component_type === "Earning").length;
                                            const dedCount = (t.items || []).filter((i) => i.component_type === "Deduction").length;
                                            return (
                                                <tr key={t.template_id}>
                                                    <td>
                                                        <div className="tpl-name" style={fontCssStyle(t.font)}>{t.template_name}</div>
                                                        <div className="tpl-font-hint">{getFont(t.font).label}</div>
                                                    </td>
                                                    <td>{earnCount}</td>
                                                    <td>{dedCount}</td>
                                                    <td className="pss-num">₹{fmt(t.gross)}</td>
                                                    <td className="pss-num pss-num--accent">₹{fmt(t.net_pay)}</td>
                                                    <td>
                                                        <button className="pss-action-btn btn-edit" onClick={() => openEditTemplate(t)}>Edit</button>
                                                        <button className="pss-action-btn btn-delete" style={{ marginLeft: 6 }} onClick={() => handleDeleteTemplate(t)}>Delete</button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <div className="ssp-actions">
                            <div />
                            <div className="ssp-actions-btns">
                                <button className="pss-btn-cancel" onClick={() => setShowTemplatesModal(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ══ TEMPLATE EDITOR MODAL (create / edit) ══ */}
            {editingTemplate && (
                <>
                    <div className="ssp-overlay" />
                    <div className="ssp-modal tpl-edit-modal">
                        <div className="ssp-modal-top">
                            <div>
                                <div className="ssp-modal-title">
                                    {editingTemplate.template_id ? "Edit Template" : "New Template"}
                                </div>
                                <div className="ssp-modal-sub">Earnings & deductions that will be applied to every selected employee</div>
                            </div>
                            <button className="ssp-close" onClick={closeTemplateEditor}><FaTimes /></button>
                        </div>
                        <div className="ssp-body tpl-edit-body">
                            {(() => {
                                // Compute totals + max rows so earnings/deductions zip into the same grid
                                const tplGross = tplEarn.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
                                const tplTotalDed = tplDed.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
                                const tplNet = tplGross - tplTotalDed;
                                const tplMaxRows = Math.max(tplEarn.length, tplDed.length);

                                // Per-cell renderer (mirrors the structure modal's renderPayCell but
                                // operates on the tplEarn/tplDed state instead)
                                const renderTplCell = (type, idx) => {
                                    const rows = type === "Earning" ? tplEarn : tplDed;
                                    const setRows = type === "Earning" ? setTplEarn : setTplDed;
                                    const options = type === "Earning" ? earnOptions : dedOptions;
                                    const row = rows[idx];
                                    if (!row) {
                                        return (<><div className="ssp-pay-cell" /><div className="ssp-pay-cell" /></>);
                                    }
                                    const usedIds = rows.map((r, i) => i === idx ? null : String(r.component_id)).filter(Boolean);
                                    const available = options.filter((o) => !usedIds.includes(String(o.component_id)));
                                    const compOpts = available.map((o) => ({ value: String(o.component_id), label: o.component_name }));
                                    if (row.component_id && !compOpts.find((o) => o.value === String(row.component_id))) {
                                        const c = options.find((o) => String(o.component_id) === String(row.component_id));
                                        if (c) compOpts.unshift({ value: String(c.component_id), label: c.component_name });
                                    }
                                    const current = compOpts.find((o) => o.value === String(row.component_id)) || null;
                                    return (
                                        <>
                                            <div className="ssp-pay-cell ssp-pay-cell-select">
                                                <div className="ssp-pay-rs-wrap">
                                                    <Select
                                                        classNamePrefix="ssp-rs"
                                                        options={compOpts}
                                                        value={current}
                                                        isSearchable
                                                        isClearable
                                                        placeholder="- select -"
                                                        styles={cellSelectStyles(false)}
                                                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                                        menuPlacement="auto"
                                                        maxMenuHeight={220}
                                                        closeMenuOnScroll={(e) => !!(e && e.target && e.target.classList && e.target.classList.contains("ssp-body"))}
                                                        onChange={(opt) => setRows((rs) => rs.map((r, i) => i === idx ? { ...r, component_id: opt ? opt.value : "" } : r))}
                                                    />
                                                </div>
                                                <button
                                                    className="ssp-pay-remove"
                                                    title="Remove"
                                                    onClick={() => setRows((rs) => rs.filter((_, i) => i !== idx))}
                                                ><FaTrash /></button>
                                            </div>
                                            <div className="ssp-pay-cell ssp-pay-cell-amount">
                                                <input
                                                    type="number"
                                                    placeholder="0"
                                                    min="0"
                                                    value={row.amount}
                                                    onWheel={(e) => e.target.blur()}
                                                    onKeyDown={(e) => { if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault(); }}
                                                    onPaste={(e) => {
                                                        const txt = (e.clipboardData || window.clipboardData).getData("text");
                                                        if (!/^\d*\.?\d*$/.test(txt)) e.preventDefault();
                                                    }}
                                                    onChange={(e) => {
                                                        let v = e.target.value;
                                                        if (v) {
                                                            // Same fix as the structure editor: only strip leading
                                                            // zeros when followed by 1-9 so backspacing the middle of
                                                            // "30000.00" doesn't collapse to ".00".
                                                            v = v.replace(/^0+(?=[1-9])/, "");
                                                            const m = v.match(/^(\d*)(?:\.(\d{0,2}))?/);
                                                            if (m) v = m[1] + (m[2] !== undefined ? "." + m[2] : "");
                                                        }
                                                        setRows((rs) => rs.map((r, i) => i === idx ? { ...r, amount: v } : r));
                                                    }}
                                                />
                                            </div>
                                        </>
                                    );
                                };

                                return (
                                    <div className="ssp-paper" style={fontCssStyle(tplFont)} ref={tplPaperRef}>
                                        {/* Full DOLLUZ CORP logo watermark (same as the structure modal / PDF).
                                            Anchored to the template-name field (stands in for the LOCATION row). */}
                                        <div
                                            className="ssp-watermark ssp-watermark--sharp"
                                            style={{ backgroundImage: `url(${DOLLUZ_Full_Logo})`, ...(tplWmTop != null ? { top: tplWmTop } : {}) }}
                                        />
                                        <div
                                            className="ssp-watermark ssp-watermark--blur"
                                            style={{ backgroundImage: `url(${DOLLUZ_Full_Logo})`, ...(tplWmTop != null ? { top: tplWmTop } : {}) }}
                                        />

                                        {/* Gold accent + full logo */}
                                        <div className="ssp-gold" />
                                        <div className="ssp-logo-wrap">
                                            <img src={DOLLUZ_Full_Logo} alt="Dolluz Corp" className="ssp-logo" />
                                        </div>

                                        <div className="ssp-title">
                                            SALARY STRUCTURE TEMPLATE
                                            {tplName ? <> - <span style={{ color: "#1e3a5f" }}>{tplName}</span></> : null}
                                        </div>

                                        {/* Template name input - replaces the employee-info table */}
                                        <div className="tpl-paper-namefield" ref={tplAnchorRef}>
                                            <label>
                                                Template Name <span style={{ color: "#dc2626" }}>*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={tplName}
                                                onChange={(e) => setTplName(e.target.value)}
                                                placeholder="e.g. Junior Developer, Senior Manager…"
                                                maxLength={100}
                                            />
                                        </div>

                                        {/* Earnings + Deductions zipped table (same layout as the structure modal) */}
                                        <div className="ssp-pay-table">
                                            <div className="ssp-pay-row ssp-pay-head">
                                                <div className="ssp-pay-cell">Earnings</div>
                                                <div className="ssp-pay-cell ssp-pay-right">Monthly in INR</div>
                                                <div className="ssp-pay-cell">Deductions</div>
                                                <div className="ssp-pay-cell ssp-pay-right">Monthly in INR</div>
                                            </div>
                                            {Array.from({ length: tplMaxRows }).map((_, i) => (
                                                <div className="ssp-pay-row" key={i}>
                                                    {renderTplCell("Earning", i)}
                                                    {renderTplCell("Deduction", i)}
                                                </div>
                                            ))}
                                            <div className="ssp-pay-row ssp-pay-addrow">
                                                <div className="ssp-pay-cell ssp-pay-cell-add">
                                                    <button onClick={() => setTplEarn((rs) => [...rs, { component_id: "", amount: "" }])}>
                                                        <FaPlus /> Add earning
                                                    </button>
                                                </div>
                                                <div className="ssp-pay-cell" />
                                                <div className="ssp-pay-cell ssp-pay-cell-add">
                                                    <button onClick={() => setTplDed((rs) => [...rs, { component_id: "", amount: "" }])}>
                                                        <FaPlus /> Add deduction
                                                    </button>
                                                </div>
                                                <div className="ssp-pay-cell" />
                                            </div>
                                            <div className="ssp-pay-row ssp-pay-totals">
                                                <div className="ssp-pay-cell">Total Earnings</div>
                                                <div className="ssp-pay-cell ssp-pay-right">{fmt(tplGross)}</div>
                                                <div className="ssp-pay-cell">Total Deductions</div>
                                                <div className="ssp-pay-cell ssp-pay-right">{fmt(tplTotalDed)}</div>
                                            </div>
                                            <div className="ssp-pay-row ssp-pay-netpay">
                                                <div className="ssp-pay-cell" />
                                                <div className="ssp-pay-cell" />
                                                <div className="ssp-pay-cell">NET PAY</div>
                                                <div className="ssp-pay-cell ssp-pay-right">{fmt(tplNet)}</div>
                                            </div>
                                        </div>

                                        {/* Note (HARDCODED, same as real payslip) */}
                                        <div className="ssp-note-static">
                                            <strong>Note:</strong> {DEFAULT_NOTE}
                                        </div>

                                        {/* Payslip Font - snapshotted onto every salary_structure
                                            created via bulk apply, then onto every payslip
                                            generated from those structures. */}
                                        <div className="ssp-field-block ssp-font-block">
                                            <label htmlFor="tpl-font-sel">Payslip Font</label>
                                            <select
                                                id="tpl-font-sel"
                                                className="ssp-font-sel"
                                                value={tplFont || DEFAULT_FONT_KEY}
                                                onChange={(e) => setTplFont(e.target.value)}
                                                style={fontCssStyle(tplFont)}
                                            >
                                                {PAYSLIP_FONTS.map((f) => (
                                                    <option key={f.key} value={f.key} style={fontCssStyle(f.key)}>
                                                        {f.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Company Address - saved on the template and snapshotted onto
                                            every salary_structure created via bulk apply. */}
                                        <div className="ssp-footer-grid">
                                            <div className="ssp-field-block">
                                                <label>Company Address</label>
                                                <textarea
                                                    className="ssp-textarea"
                                                    rows={3}
                                                    value={tplCompanyAddress}
                                                    onChange={(e) => setTplCompanyAddress(e.target.value)}
                                                    maxLength={MAX_LEN.company_address}
                                                />
                                                <div className="ssp-char-count">
                                                    {(tplCompanyAddress || "").length} / {MAX_LEN.company_address}
                                                </div>
                                            </div>
                                            <div className="ssp-sign">
                                                <img src={signature_img} alt="Signature" className="ssp-sign-img" />
                                                <div className="ssp-sign-label">AUTHORIZED SIGNATURE</div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                        <div className="ssp-actions">
                            <div />
                            <div className="ssp-actions-btns">
                                <button className="pss-btn-cancel" onClick={closeTemplateEditor} disabled={tplSaving}>Cancel</button>
                                <button className="pss-btn-save" onClick={handleSaveTemplate} disabled={tplSaving}>
                                    {tplSaving ? "Saving…" : (editingTemplate.template_id ? "Update Template" : "Create Template")}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ══ BULK APPLY MODAL ══ */}
            {showBulkApplyModal && (
                <>
                    <div className="ssp-overlay" />
                    <div className="ssp-modal bulk-modal">
                        <div className="ssp-modal-top">
                            <div>
                                <div className="ssp-modal-title">Bulk apply template</div>
                                <div className="ssp-modal-sub">
                                    {bulkStage === 1
                                        ? <>Choose a template, dates and the <strong>{selectedEmpIds.length}</strong> employee{selectedEmpIds.length !== 1 ? "s" : ""} to configure</>
                                        : <>Review &amp; edit each of the <strong>{selectedEmpIds.length}</strong> employee{selectedEmpIds.length !== 1 ? "s" : ""} before saving</>}
                                </div>
                            </div>
                            <button className="ssp-close" onClick={() => setShowBulkApplyModal(false)}><FaTimes /></button>
                        </div>
                        <div className="ssp-body bulk-body">
                            {bulkStage === 1 ? (
                            <>
                            <div className="tpl-field bulk-tpl-field">
                                <label>Choose Template <span style={{ color: "#dc2626" }}>*</span></label>
                                <div className="bulk-tpl-grid">
                                    {templates.map((t) => {
                                        const earnItems = (t.items || []).filter((i) => i.component_type === "Earning");
                                        const dedItems = (t.items || []).filter((i) => i.component_type === "Deduction");
                                        const isSelected = String(bulkTemplateId) === String(t.template_id);
                                        return (
                                            <div
                                                key={t.template_id}
                                                className={`bulk-tpl-card ${isSelected ? "bulk-tpl-card--selected" : ""}`}
                                                onClick={() => setBulkTemplateId(String(t.template_id))}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setBulkTemplateId(String(t.template_id)); } }}
                                            >
                                                <div className="bulk-tpl-card-head">
                                                    <div className={`bulk-tpl-card-radio ${isSelected ? "bulk-tpl-card-radio--selected" : ""}`}>
                                                        {isSelected && <FaCheck size={11} />}
                                                    </div>
                                                    <div className="bulk-tpl-card-name" style={fontCssStyle(t.font)}>
                                                        {t.template_name}
                                                        <span className="bulk-tpl-card-font">{getFont(t.font).label}</span>
                                                    </div>
                                                    {/* Info icon - hover/focus shows the full component breakdown (portal tooltip) */}
                                                    <span
                                                        className="bulk-tpl-card-info"
                                                        onClick={(e) => e.stopPropagation()}
                                                        tabIndex={0}
                                                        onMouseEnter={(e) => showInfoTip(e.currentTarget, t, earnItems, dedItems)}
                                                        onMouseLeave={() => setInfoTip(null)}
                                                        onFocus={(e) => showInfoTip(e.currentTarget, t, earnItems, dedItems)}
                                                        onBlur={() => setInfoTip(null)}
                                                    >
                                                        <FaInfoCircle />
                                                    </span>
                                                </div>
                                                <div className="bulk-tpl-card-stats">
                                                    <span className="bulk-tpl-card-stat bulk-tpl-card-stat--earn">
                                                        <span className="bulk-tpl-card-stat-num">{earnItems.length}</span>
                                                        earning{earnItems.length !== 1 ? "s" : ""}
                                                    </span>
                                                    <span className="bulk-tpl-card-stat bulk-tpl-card-stat--ded">
                                                        <span className="bulk-tpl-card-stat-num">{dedItems.length}</span>
                                                        deduction{dedItems.length !== 1 ? "s" : ""}
                                                    </span>
                                                </div>
                                                <div className="bulk-tpl-card-foot">
                                                    <span className="bulk-tpl-card-net-label">Net Pay</span>
                                                    <span className="bulk-tpl-card-net-val">₹{fmt(t.net_pay)}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {infoTip && createPortal(
                                <div
                                    className={`bulk-tpl-tooltip-fixed bulk-tpl-tooltip-fixed--${infoTip.place}`}
                                    style={{ top: infoTip.top, left: infoTip.left }}
                                >
                                    <div className="bulk-tpl-tooltip-cols">
                                        <div className="bulk-tpl-tooltip-section">
                                            <div className="bulk-tpl-tooltip-title bulk-tpl-tooltip-title--earn">Earnings</div>
                                            {infoTip.earnItems.length === 0 ? (
                                                <div className="bulk-tpl-tooltip-empty">none</div>
                                            ) : infoTip.earnItems.map((it) => (
                                                <div className="bulk-tpl-tooltip-row" key={it.item_id}>
                                                    <span className="bulk-tpl-tooltip-label">{it.component_name}</span>
                                                    <span className="bulk-tpl-tooltip-amt">₹{fmt(it.amount)}</span>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="bulk-tpl-tooltip-section">
                                            <div className="bulk-tpl-tooltip-title bulk-tpl-tooltip-title--ded">Deductions</div>
                                            {infoTip.dedItems.length === 0 ? (
                                                <div className="bulk-tpl-tooltip-empty">none</div>
                                            ) : infoTip.dedItems.map((it) => (
                                                <div className="bulk-tpl-tooltip-row" key={it.item_id}>
                                                    <span className="bulk-tpl-tooltip-label">{it.component_name}</span>
                                                    <span className="bulk-tpl-tooltip-amt">₹{fmt(it.amount)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="bulk-tpl-tooltip-foot">
                                        <span>Net Pay</span>
                                        <span>₹{fmt(infoTip.t.net_pay)}</span>
                                    </div>
                                </div>,
                                document.body
                            )}

                            {(() => {
                                const fromYM = parseDateToYM(bulkEffectiveFrom);
                                const toYM = parseDateToYM(bulkEffectiveTo);
                                const currentY = new Date().getFullYear();
                                // Same 2018-2050 window as the per-employee panel.
                                const yearsAll = dslipYears({ desc: true });
                                return (
                                    <div className="tpl-field">
                                        <label style={{ color: "#c2410c", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, fontSize: 12, marginBottom: 8, display: "block" }}>
                                            Effective From - To <span style={{ color: "#dc2626" }}>*</span>
                                        </label>
                                        <div className="ssp-range-row">
                                            <Select
                                                styles={rangeSelectStyles(MONTH_SEL_W, false)}
                                                {...rangeSelectProps("auto")}
                                                placeholder="From month"
                                                value={fromYM.m === "" ? null : { value: fromYM.m, label: MONTH_LABELS[fromYM.m] }}
                                                options={MONTH_LABELS.map((label, i) => ({ value: i, label }))}
                                                onChange={(opt) => {
                                                    const y = fromYM.y || currentY;
                                                    setBulkEffectiveFrom(firstOfMonth(y, opt.value));
                                                }}
                                            />
                                            <Select
                                                styles={rangeSelectStyles(YEAR_SEL_W, false)}
                                                {...DSLIP_YEAR_SELECT}
                                                {...rangeSelectProps("auto")}
                                                placeholder="Year"
                                                value={fromYM.y ? { value: fromYM.y, label: String(fromYM.y) } : null}
                                                options={yearsAll.map((y) => ({ value: y, label: String(y) }))}
                                                onChange={(opt) => {
                                                    const m = fromYM.m === "" ? 0 : fromYM.m;
                                                    setBulkEffectiveFrom(firstOfMonth(opt.value, m));
                                                }}
                                            />
                                            <span className="ssp-range-sep">→</span>
                                            <Select
                                                styles={rangeSelectStyles(MONTH_SEL_W, false)}
                                                {...rangeSelectProps("auto")}
                                                placeholder="To month"
                                                value={toYM.m === "" ? null : { value: toYM.m, label: MONTH_LABELS[toYM.m] }}
                                                options={MONTH_LABELS.map((label, i) => ({ value: i, label }))}
                                                onChange={(opt) => {
                                                    const y = toYM.y || fromYM.y || currentY;
                                                    setBulkEffectiveTo(lastOfMonth(y, opt.value));
                                                }}
                                            />
                                            <Select
                                                styles={rangeSelectStyles(YEAR_SEL_W, false)}
                                                {...DSLIP_YEAR_SELECT}
                                                {...rangeSelectProps("auto")}
                                                placeholder="Year"
                                                value={toYM.y ? { value: toYM.y, label: String(toYM.y) } : null}
                                                options={yearsAll.map((y) => ({ value: y, label: String(y) }))}
                                                onChange={(opt) => {
                                                    const m = toYM.m === "" ? 0 : toYM.m;
                                                    setBulkEffectiveTo(lastOfMonth(opt.value, m));
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })()}

                            <div className="bulk-emp-list">
                                <div className="bulk-emp-list-title">Selected employees ({selectedEmpIds.length})</div>
                                <div className="bulk-emp-chips">
                                    {selectedEmpIds.map((id) => {
                                        const emp = employees.find((e) => e.emp_id === id);
                                        return emp ? (
                                            <span key={id} className="bulk-emp-chip">
                                                {emp.emp_first_name} {emp.emp_last_name}
                                                <span className="bulk-emp-chip-id">{emp.emp_id}</span>
                                            </span>
                                        ) : null;
                                    })}
                                </div>
                            </div>
                            </>
                            ) : (
                            <div className="bulk-ed-list">
                                <div className="bulk-ed-hint">
                                    Values are populated from <strong>{templates.find((t) => String(t.template_id) === String(bulkTemplateId))?.template_name || "the template"}</strong>. Edit each employee's components/amounts, then Save. Net Pay must be greater than zero for each.
                                </div>
                                {selectedEmpIds.map((id) => {
                                    const emp = employees.find((e) => e.emp_id === id);
                                    const it = bulkEmpItems[id] || { earnings: [], deductions: [] };
                                    const net = bulkEmpNet(id);
                                    const renderRow = (type, rows, idx) => {
                                        const options = type === "Earning" ? earnOptions : dedOptions;
                                        const row = rows[idx];
                                        const available = availableOptions(options, rows, idx);
                                        const compOpts = available.map((o) => ({ value: String(o.component_id), label: o.component_name }));
                                        if (row.component_id && !compOpts.find((o) => o.value === String(row.component_id))) {
                                            const c = options.find((o) => String(o.component_id) === String(row.component_id));
                                            if (c) compOpts.unshift({ value: String(c.component_id), label: c.component_name });
                                        }
                                        const current = compOpts.find((o) => o.value === String(row.component_id)) || null;
                                        return (
                                            <div className="bulk-ed-row" key={idx}>
                                                <div className="bulk-ed-sel">
                                                    <Select
                                                        classNamePrefix="ssp-rs"
                                                        options={compOpts}
                                                        value={current}
                                                        isSearchable
                                                        isClearable
                                                        placeholder="- select -"
                                                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                                        menuPlacement="auto"
                                                        maxMenuHeight={200}
                                                        styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
                                                        onChange={(opt) => setBulkRow(id, type, idx, { component_id: opt ? opt.value : "" })}
                                                    />
                                                </div>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className="bulk-ed-amt"
                                                    placeholder="0"
                                                    value={row.amount}
                                                    onWheel={(e) => e.target.blur()}
                                                    onKeyDown={(e) => { if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault(); }}
                                                    onChange={(e) => {
                                                        let v = e.target.value;
                                                        if (v) { v = v.replace(/^0+(?=[1-9])/, ""); const m = v.match(/^(\d*)(?:\.(\d{0,2}))?/); if (m) v = m[1] + (m[2] !== undefined ? "." + m[2] : ""); }
                                                        setBulkRow(id, type, idx, { amount: v });
                                                    }}
                                                />
                                                <button className="bulk-ed-del" title="Remove" onClick={() => removeBulkRow(id, type, idx)}><FaTrash /></button>
                                            </div>
                                        );
                                    };
                                    return (
                                        <div className="bulk-ed-card" key={id}>
                                            <div className="bulk-ed-head">
                                                <div className="bulk-ed-name">{emp ? `${emp.emp_first_name} ${emp.emp_last_name}` : id}<span className="bulk-ed-id">{id}</span></div>
                                                <div className={`bulk-ed-net ${net <= 0 ? "bulk-ed-net--bad" : ""}`}>Net Pay ₹{fmt(net)}</div>
                                            </div>
                                            <div className="bulk-ed-cols">
                                                <div className="bulk-ed-col">
                                                    <div className="bulk-ed-col-title bulk-ed-col-title--earn">Earnings</div>
                                                    {it.earnings.map((r, i) => renderRow("Earning", it.earnings, i))}
                                                    <button className="bulk-ed-add" onClick={() => addBulkRow(id, "Earning")}><FaPlus /> Add earning</button>
                                                </div>
                                                <div className="bulk-ed-col">
                                                    <div className="bulk-ed-col-title bulk-ed-col-title--ded">Deductions</div>
                                                    {it.deductions.map((r, i) => renderRow("Deduction", it.deductions, i))}
                                                    <button className="bulk-ed-add" onClick={() => addBulkRow(id, "Deduction")}><FaPlus /> Add deduction</button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            )}
                        </div>
                        <div className="ssp-actions">
                            <div>
                                {bulkStage === 2 && (
                                    <button className="pss-btn-cancel" onClick={() => setBulkStage(1)} disabled={bulkSaving}>← Back</button>
                                )}
                            </div>
                            <div className="ssp-actions-btns">
                                <button className="pss-btn-cancel" onClick={() => setShowBulkApplyModal(false)} disabled={bulkSaving}>Cancel</button>
                                {bulkStage === 1 ? (
                                    <button className="pss-btn-save" onClick={handleBulkNext}>
                                        Next: review &amp; edit →
                                    </button>
                                ) : (
                                    <button className="pss-btn-save" onClick={handleBulkApply} disabled={bulkSaving}>
                                        {bulkSaving ? "Saving…" : `Save ${selectedEmpIds.length} salary structure${selectedEmpIds.length !== 1 ? "s" : ""}`}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default Payslip_Salary_Structure;
